import { sessionFetch, setSessionRenewal } from "./session-fetch.js";
import { MicrosoftSignIn, loadMicrosoftConfig, SIGNED_OUT } from "./microsoft-auth.js";
import { ChatHistorySync, type HistorySnapshot } from "./chat-history-sync.js";
import { completedActivityRun } from "./activity.js";
import { renderQuestions, renderPollEntry, renderStorylineEntry, applyStorylineState, createActivityDots } from "./transcript-cards.js";
import type { Question } from "./transcript-cards.js";
import { insertSlides, buildSlides, dropBlankSlides, deleteSlidesById, UncertainInsertError } from "./insert-deck.js";
import type { DeckBuild } from "./insert-deck.js";
import {
  slideThumbnail,
  toolIcon,
  toolLabel,
  ICONS,
  ATTACHMENT_ICONS,
} from "./presets.js";
import {
  applyNativeActions,
  checkChanges,
  NativeEditError,
  CopyStalledError,
  inspectPresentation,
  readPresentation,
  renderPresentation,
  searchPresentation,
  setSydaniPageSize,
  copySlide,
  slideOrder,
  powerPointActivity,
  presentationTag,
  showSlide,
  trackPowerPointRuns,
  SYDANI_SIZE,
} from "./native-powerpoint.js";
import type { NativeAction, PresentationProfile, SlideCopy } from "./native-powerpoint.js";
import { renderMarkdown } from "./markdown.js";
import { DeadlineError, StoppedError, WriteStalledError, hedged, withinTime } from "./deadline.js";
import { normaliseAssistantResponse } from "./response-contract.js";
import { conversationOutcome, sanitiseStoredEntries } from "./transcript-history.js";
import type { TranscriptEntry, PollAnswer } from "./transcript-history.js";

type DynamicRecord = Record<string, any>;
type ChatEntry = TranscriptEntry | (DynamicRecord & { kind: string });
type ClientChange = NativeAction & {
  slide?: Record<string, unknown>;
  /** A copy of one of the user's slides: its source, and the id edits use. */
  copyOf?: string;
  copyId?: string;
  /** The existing slide a new slide follows; the end of the deck when unset. */
  afterSlideId?: string | null;
  section?: string;
  thumbnail?: string;
  design?: string;
};

interface Conversation {
  id: string;
  title: string;
  entries: ChatEntry[];
  previousResponseId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FailedRequest { message: string; files: File[]; entry?: ChatEntry }
interface UiAttachment {
  kind: "file" | "component";
  name: string;
  size?: number;
  shapeName?: string;
  shapeId?: string;
  slideNumber?: number | null;
}
interface SessionError extends Error {
  resetResponseId?: boolean;
  /** Where a failed turn got to, so the next message continues from there. */
  responseId?: string;
  auditTurnId?: string;
  kind?: "configuration";
}
interface StorylineHandle {
  entry: DynamicRecord & {
    kind: string;
    slides: DynamicRecord[];
    built: number;
    designs: string[];
    live: boolean;
    superseded?: boolean;
  };
  element: HTMLElement;
  list: HTMLOListElement;
}
interface ActivityStepHandle { item: HTMLElement; step: DynamicRecord; entry: ChatEntry }
interface ActivityGroupHandle { entry: DynamicRecord; element: HTMLElement; list: HTMLElement; count: number }

interface AppState {
  files: File[];
  previousResponseId: string | null;
  presentation: PresentationProfile | null;
  pendingChanges: ClientChange[];
  pendingAuditTurnId: string | null;
  conversations: Conversation[];
  activeConversationId: string | null;
  chatHistory: ChatEntry[];
  presentationId: string | null;
  authenticated: boolean;
  busy: boolean;
  applying: boolean;
  applyNeedsReview: boolean;
  /** The review card asked the user to approve the 4:3 slide size change. */
  canvasConversionShown: boolean;
  stopping: boolean;
  ready: boolean;
  awaitingAnswer: boolean;
  /** The request behind each failure card still on screen, by the card's entry id. */
  failedRequests: Map<string, FailedRequest>;
  presentationName: string;
}

const state: AppState = {
  files: [],
  previousResponseId: null,
  presentation: null,
  pendingChanges: [],
  pendingAuditTurnId: null,
  conversations: [],
  activeConversationId: null,
  chatHistory: [],
  presentationId: null,
  authenticated: false,
  busy: false,
  applying: false,
  applyNeedsReview: false,
  canvasConversionShown: false,
  stopping: false,
  ready: false,
  awaitingAnswer: false,
  failedRequests: new Map(),
  presentationName: "Current presentation",
};

// Every selector below names a required element in taskpane.html. Failing
// immediately is safer than carrying a nullable element through the controller.
const $ = (selector: string): any => {
  const element = document.querySelector(selector);
  if (!element) throw new Error(`Missing required task-pane element: ${selector}`);
  return element;
};
const messages = $("#messages");
const conversationScroll = $("#conversation-scroll");

function scrollConversationToEnd(): void {
  conversationScroll.scrollTop = conversationScroll.scrollHeight;
}
const DOCUMENT_ID_KEY = "SYDANI_AI_DOCUMENT_ID";
const LOGIN_CREDENTIAL_ERROR = "That email and password do not match. Check the password and try again.";
let contextRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let contextRefreshQueue: Promise<unknown> = Promise.resolve();
let activeTurnController: AbortController | null = null;
let chatSaveRevision = 0;
let savedChatRevision = 0;
const chatHistorySync = new ChatHistorySync();
// Chats changed since the server last accepted them, besides the open one. A
// save serialises only these and the open chat, not every stored chat.
const unsavedConversations = new Set<string>();
let chatSaveTimer: ReturnType<typeof setTimeout> | null = null;
let chatSavePromise: Promise<void> | null = null;
let contextRefreshRegistered = false;

function changeTypeCounts(changes: ClientChange[]): Record<string, number> {
  return (changes || []).reduce((counts, change) => {
    const type = change?.type || "unknown";
    counts[type] = (counts[type] || 0) + 1;
    return counts;
  }, {} as Record<string, number>);
}

async function auditClientEvent(event: string, data: DynamicRecord = {}, turnId = state.pendingAuditTurnId): Promise<void> {
  try {
    await sessionFetch("/api/audit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, turnId, data }),
      signal: AbortSignal.timeout(5000),
    });
  } catch {
    // Auditing must never block the presentation workflow.
  }
}

function newUuid(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function errorMessage(error: unknown, fallback = "Unknown error"): string {
  return error instanceof Error ? error.message : String(error || fallback);
}

function entrySize(entry: ChatEntry): number {
  // Entries are no longer all text, so they are measured by what they will
  // occupy in storage rather than by a field only messages have.
  try {
    return JSON.stringify(entry).length;
  } catch {
    return 0;
  }
}

function retainedHistory(history: ChatEntry[]): ChatEntry[] {
  const retained: ChatEntry[] = [];
  let characterCount = 0;
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const entry = history[index]!;
    const size = entrySize(entry);
    if (retained.length >= 160 || characterCount + size > 200000) break;
    retained.unshift(entry);
    characterCount += size;
  }
  return retained;
}

/** Superseded plans remain stored for audit, but only the canonical revision
 * belongs in the readable transcript or any app-owned context derived from it. */
function visibleTranscriptEntries(history: ChatEntry[]): ChatEntry[] {
  return history.filter((entry) => !(
    entry.kind === "storyline" && (entry as DynamicRecord).superseded === true
  ));
}

function conversationTitle(history: ChatEntry[]): string {
  const firstPrompt = history.find(
    (entry) => entry.kind === "message" && entry.role === "user");
  const firstPromptText = firstPrompt && "text" in firstPrompt ? firstPrompt.text : "";
  const firstPromptValue = typeof firstPromptText === "string" ? firstPromptText : "";
  const compact = firstPromptValue.replace(/\s+/g, " ").trim();
  if (!compact) return "New chat";
  return compact.length > 44 ? `${compact.slice(0, 43).trimEnd()}…` : compact;
}

function isStartedConversation(conversation: Conversation | null | undefined): boolean {
  return conversation?.entries?.some(
    (entry) => entry.kind === "message" && entry.role === "user") || false;
}

function createConversation({
  entries: initialEntries = [],
  previousResponseId = null,
  createdAt,
  updatedAt,
}: {
  entries?: ChatEntry[];
  previousResponseId?: string | null;
  createdAt?: string;
  updatedAt?: string;
} = {}): Conversation {
  const now = new Date().toISOString();
  const chatHistory = retainedHistory(initialEntries);
  return {
    id: newUuid(),
    title: conversationTitle(chatHistory),
    entries: chatHistory,
    previousResponseId: typeof previousResponseId === "string" ? previousResponseId : null,
    createdAt: createdAt || now,
    updatedAt: updatedAt || now,
  };
}

function activeConversation(): Conversation | null {
  return state.conversations.find((conversation) => conversation.id === state.activeConversationId) || null;
}

function syncActiveConversation() {
  const conversation = activeConversation();
  if (!conversation) return;
  conversation.entries = retainedHistory(state.chatHistory);
  conversation.previousResponseId = state.previousResponseId;
  conversation.title = conversationTitle(conversation.entries);
}

function touchConversationTimestamp() {
  const conversation = activeConversation();
  if (conversation) conversation.updatedAt = new Date().toISOString();
}

function ensureEntryMetadata(entry: ChatEntry): void {
  const now = new Date().toISOString();
  entry.id ||= newUuid();
  entry.createdAt ||= now;
  entry.updatedAt ||= now;
}

function storedEntrySnapshot(entry: ChatEntry): { id: string; [key: string]: unknown } {
  ensureEntryMetadata(entry);
  const { id, kind, role, turnId, createdAt, updatedAt, ...payload } = entry as DynamicRecord;
  return {
    id,
    kind,
    role: role || null,
    turnId: turnId || null,
    payload,
    createdAt,
    updatedAt,
  };
}

function tagTurnEntries(start: number, turnId: string | null): void {
  if (!turnId) return;
  const now = new Date().toISOString();
  state.chatHistory.slice(start).forEach((entry) => {
    ensureEntryMetadata(entry);
    entry.turnId ||= turnId;
    entry.updatedAt = now;
  });
  touchConversationTimestamp();
  syncActiveConversation();
  saveChatHistory();
}

/** Every kept chat's metadata, but entries only for the open chat and those in
 * `include`; pass null for all of them. */
function chatHistorySnapshot(include: ReadonlySet<string> | null): HistorySnapshot {
  syncActiveConversation();
  const ranked = state.conversations
    .filter(isStartedConversation)
    .sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)));
  const selected = activeConversation();
  const savedSelection = isStartedConversation(selected) ? selected : null;
  const conversations = [savedSelection, ...ranked.filter((conversation) => conversation !== savedSelection)]
    .filter((conversation): conversation is Conversation => conversation !== null)
    .slice(0, 30);
  chatHistorySync.retain(conversations.map((conversation) => conversation.id));
  const included = conversations.filter((conversation) => !include
    || conversation === savedSelection || include.has(conversation.id));
  return {
    version: 2,
    activeConversationId: savedSelection?.id || null,
    conversations: included.map((conversation) => ({
      id: conversation.id,
      title: conversation.title,
      // Kept within the retention limit on load and on every sync already.
      entries: conversation.entries.map(storedEntrySnapshot),
      previousResponseId: conversation.previousResponseId,
      createdAt: conversation.createdAt,
      updatedAt: conversation.updatedAt,
    })),
  };
}

function showHistorySaveError(message: string | null): void {
  $("#history-save-error").classList.toggle("hidden", !message);
  $("#history-save-message").textContent = message || "";
}

async function persistChatHistory(): Promise<void> {
  let claimed = new Set<string>();
  try {
    while (savedChatRevision < chatSaveRevision && state.authenticated && state.presentationId) {
      const targetRevision = chatSaveRevision;
      claimed = new Set(unsavedConversations);
      unsavedConversations.clear();
      const pending = chatHistorySync.pending(chatHistorySnapshot(claimed));
      const response = await sessionFetch(
        `/api/presentations/${encodeURIComponent(state.presentationId)}/chat-history`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(pending),
        },
      );
      if (response.status === 401) {
        showSignedOut();
        throw new Error("Your session expired before the chat was saved. Sign in again.");
      }
      if (!response.ok) throw new Error(`Chat history save returned ${response.status}`);
      chatHistorySync.acknowledge(pending);
      savedChatRevision = targetRevision;
      claimed.clear();
    }
    showHistorySaveError(null);
  } catch (error) {
    // The next attempt still owes the server these chats.
    claimed.forEach((id) => unsavedConversations.add(id));
    const message = state.authenticated
      ? "Your chat has not been saved. Retry before closing the pane."
      : errorMessage(error);
    if (state.authenticated) showHistorySaveError(message);
    throw new Error(message, { cause: error });
  }
}

function flushChatHistory(): Promise<void> {
  // A caller asking to flush needs the newest transcript in PostgreSQL now,
  // not after the ordinary debounce. This matters for decisions such as poll
  // answers that must survive an immediate pane close.
  if (chatSaveTimer) {
    clearTimeout(chatSaveTimer);
    chatSaveTimer = null;
  }
  // Reuse the active write so callers such as sign-out wait for it instead of
  // invalidating the session while the transcript is still being persisted.
  if (chatSavePromise) return chatSavePromise;
  if (!state.authenticated || !state.presentationId) return Promise.resolve();
  chatSavePromise = persistChatHistory().finally(() => {
    chatSavePromise = null;
  });
  return chatSavePromise;
}

function saveChatHistory(): void {
  if (!state.ready || !state.authenticated || !state.presentationId) return;
  chatSaveRevision += 1;
  if (chatSaveTimer) clearTimeout(chatSaveTimer);
  chatSaveTimer = setTimeout(() => {
    chatSaveTimer = null;
    void flushChatHistory().catch(() => {});
  }, 120);
}

async function presentationDocumentId(): Promise<string> {
  // Document settings did not survive reopening on PowerPoint for the web, so
  // every opening looked like a new presentation and its chats were never
  // found. The id now lives in a tag inside the file. One already in settings
  // is kept, so a deck whose settings did persist keeps its chats.
  const stored = Office.context.document.settings.get(DOCUMENT_ID_KEY);
  const candidate = typeof stored === "string" && stored ? stored : newUuid();
  return withinTime(presentationTag(DOCUMENT_ID_KEY, candidate), PRESENTATION_CHECK_MS,
    "PowerPoint did not respond while opening this presentation's chats.");
}

async function loadChatHistory(): Promise<void> {
  state.presentationId = await presentationDocumentId();
  const response = await sessionFetch(
    `/api/presentations/${encodeURIComponent(state.presentationId)}/chat-history`,
  );
  if (response.status === 401) {
    showSignedOut();
    return;
  }
  if (!response.ok) throw new Error(`Chat history load returned ${response.status}`);
  const parsed: unknown = await response.json();
  const stored = parsed && typeof parsed === "object" ? parsed as DynamicRecord : null;

  if (stored?.version === 2 && Array.isArray(stored.conversations)) {
    state.conversations = stored.conversations.slice(0, 30).map((storedConversation) => {
      const conversation = createConversation({
        entries: sanitiseStoredEntries(storedConversation?.entries),
        previousResponseId: storedConversation?.previousResponseId,
        createdAt: storedConversation?.createdAt,
        updatedAt: storedConversation?.updatedAt,
      });
      if (typeof storedConversation?.id === "string") conversation.id = storedConversation.id;
      return conversation;
    });
  } else {
    state.conversations = [];
  }

  const storedActiveId = typeof stored?.activeConversationId === "string" ? stored.activeConversationId : null;
  const restoredActiveId = state.conversations.some((conversation) => conversation.id === storedActiveId)
    ? storedActiveId
    : null;
  if (!restoredActiveId) state.conversations.unshift(createConversation());
  const initialConversationId = restoredActiveId || state.conversations[0]!.id;
  state.activeConversationId = null;
  activateConversation(initialConversationId, false);
  chatSaveRevision = 0;
  savedChatRevision = 0;
  unsavedConversations.clear();
  chatHistorySync.reset(chatHistorySnapshot(null));
}

let microsoftSignIn: MicrosoftSignIn | null = null;
let currentUserId: string | null = null;

let signingIn = false;

// How the user last signed in here, kept across panes. A Microsoft session
// ends with Microsoft's token, about an hour after sign-in; an open pane then
// signs in again quietly, and a pane opened later does the same before it
// shows the sign-in screen. Signing out on purpose forgets it.
const LAST_SIGN_IN = "deck-studio-last-sign-in";

function rememberSignIn(method: "microsoft" | "password" | null): void {
  try {
    if (method) localStorage.setItem(LAST_SIGN_IN, method);
    else localStorage.removeItem(LAST_SIGN_IN);
  } catch { /* Without storage the sign-in screen shows, as before. */ }
}

function lastSignIn(): string | null {
  try { return localStorage.getItem(LAST_SIGN_IN); } catch { return null; }
}

/** Sign in with Microsoft without showing anything; false when that cannot be done. */
async function quietMicrosoftSignIn(): Promise<boolean> {
  if (!microsoftSignIn || lastSignIn() !== "microsoft") return false;
  try {
    const token = await microsoftSignIn.token(false);
    if (!token) return false;
    await exchangeMicrosoftToken(token);
    return true;
  } catch {
    return false;
  }
}

async function configureMicrosoftSignIn(): Promise<void> {
  const config = await loadMicrosoftConfig();
  microsoftSignIn = config.enabled ? new MicrosoftSignIn(config) : null;
  $("#microsoft-login-button").classList.toggle("hidden", !config.enabled);

  setSessionRenewal(config.enabled ? async () => {
    if (!state.authenticated || !currentUserId || signingIn || !microsoftSignIn
        || sessionStorage.getItem("deck-studio-login-method") !== "microsoft") return false;
    const token = await microsoftSignIn.token(false);
    if (!token) return false;
    const response = await fetch("/api/auth/microsoft", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken: token, expectedUserId: currentUserId }),
    });
    return response.ok;
  } : null);
  if (config.enabled) $("#login-description").textContent = "Sign in with your email and password or your Microsoft account.";
}

async function microsoftLogin(interactive: boolean): Promise<void> {
  if (!microsoftSignIn || signingIn) return;
  signingIn = true;
  const button = $("#microsoft-login-button");
  button.disabled = true;
  $("#microsoft-login-label").textContent = "Signing in…";
  setLoginError(null);
  try {
    const token = await microsoftSignIn.token(interactive);
    if (token) await exchangeMicrosoftToken(token);
  } catch (error) {
    if (interactive) setLoginError(errorMessage(error));
  } finally {
    signingIn = false;
    button.disabled = false;
    $("#microsoft-login-label").textContent = "Continue with Microsoft";
  }
}

async function exchangeMicrosoftToken(token: string): Promise<void> {
  const response = await fetch("/api/auth/microsoft", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ accessToken: token }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Microsoft sign-in could not be completed. Please try again.");
  }
  $("#login-password").value = "";
  sessionStorage.setItem("deck-studio-login-method", "microsoft");
  sessionStorage.removeItem(SIGNED_OUT);
  rememberSignIn("microsoft");
  // Confirm this embedded browser accepts the session cookie before opening chats.
  if (!await restoreAuthentication(false)) {
    throw new Error("Allow cookies for Deck Studio in your browser, then sign in again.");
  }
}

function setLoginError(message: string | null): void {
  const error = $("#login-error");
  const card = $("#login-form");
  error.classList.toggle("hidden", !message);
  card.classList.toggle("invalid", Boolean(message));
  if (message) error.querySelector("span").textContent = message;
}

function setCompatibilityNotice(message: string | null): void {
  const notice = $("#compatibility");
  notice.classList.toggle("hidden", !message);
  if (message) notice.textContent = message;
  else notice.textContent = "";
}

function showAppLoading(message: string): void {
  $("#app-loading-message").textContent = message;
  $("#app-loading").setAttribute("aria-busy", "true");
  document.body.classList.add("loading-app");
}

function hideAppLoading(): void {
  document.body.classList.remove("loading-app");
  $("#app-loading").setAttribute("aria-busy", "false");
}

function showSignedOut(): void {
  // A session can end in the middle of a turn (a save answered 401). The
  // turn must end with it, or it keeps writing into whatever chat is loaded
  // after the next sign-in and can leave the composer waiting on a poll that
  // no longer exists.
  activeTurnController?.abort();
  state.awaitingAnswer = false;
  showHistorySaveError(null);
  if (chatSaveTimer) {
    clearTimeout(chatSaveTimer);
    chatSaveTimer = null;
  }
  state.authenticated = false;
  currentUserId = null;
  state.ready = false;
  state.presentationId = null;
  state.conversations = [];
  state.activeConversationId = null;
  state.chatHistory = [];
  state.previousResponseId = null;
  state.pendingChanges = [];
  state.files = [];
  state.failedRequests.clear();
  chatSaveRevision = 0;
  savedChatRevision = 0;
  chatHistorySync.reset();
  unsavedConversations.clear();
  hideAppLoading();
  document.body.classList.remove("authenticated");
  $("#chat-history-button").disabled = true;
  $("#new-chat-button").disabled = true;
  $("#prompt").disabled = true;
  closeConversationPanel();
  messages.replaceChildren();
  exposeChanges([]);
  renderFiles();
  setBusy(false);
  setTimeout(() => $(microsoftSignIn ? "#microsoft-login-button" : "#login-email").focus(), 0);
}

function showSignedIn(): void {
  state.authenticated = true;
  document.body.classList.add("authenticated");
  setLoginError(null);
  // A failed session restore can leave this notice visible while the login
  // form remains usable. A successful login proves that failure is stale.
  setCompatibilityNotice(null);
}

async function startWorkspace(): Promise<void> {
  showSignedIn();
  showAppLoading("Loading your chats and presentation…");
  try {
    try {
      await loadChatHistory();
    } catch (error) {
      if (!(error instanceof DeadlineError)) throw error;
      // Without the id saved in the presentation, its chats cannot be found,
      // and a chat started under a made-up id would be lost on the next opening.
      void auditClientEvent("powerpoint.unresponsive", { phase: "startup_chats", ...powerPointActivity() }, null);
      setCompatibilityNotice("PowerPoint is not responding, so this presentation's chats could not be opened. "
        + "Close Deck Studio and open it again.");
      return;
    }
    if (!state.authenticated) return;
    renderEmptyState();
    refreshPresentationName();
    try {
      // The chat opens without it if PowerPoint is slow to answer: the first
      // message checks the presentation again before anything is sent.
      await withinTime(refreshPresentationProfile(), PRESENTATION_CHECK_MS, "PowerPoint is slow to respond.");
    } catch (error) {
      if (error instanceof DeadlineError) {
        void auditClientEvent("powerpoint.unresponsive", { phase: "startup", ...powerPointActivity() }, null);
      } else {
        setCompatibilityNotice(`PowerPoint context is unavailable: ${errorMessage(error)}`);
      }
    }
    registerAutomaticContextRefresh();
    state.ready = true;
    setBusy(false);
    setTimeout(focusPrompt, 50);
  } finally {
    hideAppLoading();
    // The chat was restored while the loading screen hid it, when it had no
    // height to scroll, so it would otherwise open at its first message.
    scrollConversationToEnd();
  }
}

async function restoreAuthentication(quiet = true): Promise<boolean> {
  const response = await fetch("/api/auth/session");
  if (response.status === 401) {
    if (quiet && await quietMicrosoftSignIn()) return true;
    showSignedOut();
    return false;
  }
  if (!response.ok) throw new Error(`Authentication returned ${response.status}`);
  const payload = await response.json();
  currentUserId = payload.user?.id || null;
  await startWorkspace();
  return true;
}

async function signIn(): Promise<void> {
  if (signingIn) return;
  signingIn = true;
  const button = $("#login-button");
  button.disabled = true;
  button.textContent = "Signing in…";
  setLoginError(null);
  setCompatibilityNotice(null);
  try {
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: $("#login-email").value,
        password: $("#login-password").value,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      setLoginError(response.status === 401
        ? LOGIN_CREDENTIAL_ERROR
        : payload.error || "Sign in could not be completed. Please try again.");
      $("#login-password").select();
      return;
    }
    $("#login-password").value = "";
    currentUserId = payload.user?.id || null;
    sessionStorage.setItem("deck-studio-login-method", "password");
    sessionStorage.removeItem(SIGNED_OUT);
    rememberSignIn("password");
    await startWorkspace();
  } catch (error) {
    showSignedOut();
    setLoginError("Deck Studio is unavailable right now. Please try again.");
  } finally {
    button.disabled = false;
    button.textContent = "Sign in";
    signingIn = false;
  }
}

async function signOut(): Promise<void> {
  if (state.applying || state.busy) return;
  if (chatSaveTimer) {
    clearTimeout(chatSaveTimer);
    chatSaveTimer = null;
  }
  try { await flushChatHistory(); } catch { return; }
  try {
    const response = await fetch("/api/auth/logout", { method: "POST" });
    if (!response.ok) throw new Error("Sign out failed");
    sessionStorage.setItem(SIGNED_OUT, "1");
    rememberSignIn(null);
    showSignedOut();
  } catch {
    setCompatibilityNotice("Sign out could not be completed. Please try again.");
  }
}

function presentationName() {
  return state.presentationName;
}

function fileNameFromUrl(value: unknown): string {
  const raw = String(value || "");
  const leaf = raw.split(/[\\/]/).at(-1)?.split(/[?#]/, 1)[0] || "";
  try {
    return decodeURIComponent(leaf) || "Current presentation";
  } catch {
    return leaf || "Current presentation";
  }
}

function refreshPresentationName() {
  if (typeof Office.context.document?.getFilePropertiesAsync !== "function") return;
  Office.context.document.getFilePropertiesAsync((result) => {
    if (result.status !== Office.AsyncResultStatus.Succeeded) return;
    state.presentationName = fileNameFromUrl(result.value?.url);
    renderOpenConversationList();
  });
}

function relativeConversationTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const elapsed = now.getTime() - date.getTime();
  if (elapsed >= 0 && elapsed < 60 * 60 * 1000) return "Just now";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { day: "numeric", month: "short" });
}

function renderConversationList() {
  const list = $("#conversation-list");
  list.replaceChildren();
  $("#conversation-document").textContent = presentationName();
  const savedConversations = state.conversations
    .filter(isStartedConversation)
    .sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)))
  if (!savedConversations.length) {
    const empty = document.createElement("p");
    empty.className = "conversation-empty";
    empty.textContent = "No previous chats";
    list.append(empty);
    return;
  }
  savedConversations.forEach((conversation) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `conversation-item${conversation.id === state.activeConversationId ? " active" : ""}`;
      const body = document.createElement("span");
      body.className = "conversation-item-body";
      const title = document.createElement("span");
      title.className = "conversation-title";
      title.textContent = conversation.title || "New chat";
      const outcome = document.createElement("span");
      outcome.className = "conversation-outcome";
      outcome.textContent = conversationOutcome(sanitiseStoredEntries(conversation.entries));
      if (outcome.textContent === "Discarded without applying") outcome.classList.add("discarded");
      body.append(title, outcome);
      const time = document.createElement("time");
      time.className = "conversation-time";
      time.dateTime = conversation.updatedAt;
      time.textContent = relativeConversationTime(conversation.updatedAt);
      button.append(body, time);
      button.addEventListener("click", () => activateConversation(conversation.id));
      list.append(button);
    });
}

function conversationPanelOpen(): boolean {
  return !$("#conversation-panel").classList.contains("hidden");
}

/** The list is rebuilt when the panel opens, so a closed one is left alone. */
function renderOpenConversationList(): void {
  if (conversationPanelOpen()) renderConversationList();
}

function closeConversationPanel() {
  $("#conversation-panel").classList.add("hidden");
  $("#chat-history-button").setAttribute("aria-expanded", "false");
  $("main").classList.remove("chats-open");
}

function activateConversation(conversationId: string, persist = true): void {
  if (state.busy || state.applying) return;
  syncActiveConversation();
  const current = activeConversation();
  const conversation = state.conversations.find((item) => item.id === conversationId);
  if (!conversation) return;
  // Its last changes may not be saved yet, and it is no longer the open chat.
  if (current) unsavedConversations.add(current.id);
  if (current && current.id !== conversationId && !isStartedConversation(current)) {
    state.conversations = state.conversations.filter((item) => item.id !== current.id);
  }
  state.activeConversationId = conversation.id;
  state.chatHistory = conversation.entries;
  state.previousResponseId = conversation.previousResponseId;
  state.files = [];
  // A failure card in another chat retries a message of that chat.
  state.failedRequests.clear();
  // Everything the turn produced comes back except this: an unapproved change
  // is an offer to touch the presentation, and it does not outlive the moment
  // it was made.
  state.pendingChanges = [];
  messages.replaceChildren();
  // Everything the turn produced comes back: the storyline, the questions and
  // their answers, what the model did, and every applied or discarded outcome.
  visibleTranscriptEntries(state.chatHistory)
    .forEach((entry) => appendEntry(entry, false));
  exposeChanges([]);
  renderFiles();
  closeConversationPanel();
  syncEmptyState();
  if (persist) saveChatHistory();
  setTimeout(focusPrompt, 0);
}

function createNewConversation() {
  if (!state.ready || state.busy || state.applying) return;
  syncActiveConversation();
  const current = activeConversation();
  if (current && !isStartedConversation(current)) {
    current.entries = [];
    current.previousResponseId = null;
    current.updatedAt = new Date().toISOString();
    state.chatHistory = current.entries;
    state.previousResponseId = null;
    activateConversation(current.id);
    return;
  }
  const conversation = createConversation();
  state.conversations.unshift(conversation);
  state.conversations = state.conversations.slice(0, 30);
  state.activeConversationId = conversation.id;
  state.chatHistory = conversation.entries;
  state.previousResponseId = null;
  activateConversation(conversation.id);
}

const EMPTY_STARTERS = [
  {
    prompt: "Turn a concept note into a deck for a partner",
    icon: '<path d="M9 1.5H4.2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h7.6a1 1 0 0 0 1-1V5.2z"></path><path d="M9 1.5v3.7h3.8"></path>',
  },
  {
    prompt: "Summarise this quarter into five slides",
    icon: '<rect x="1.8" y="3" width="12.4" height="10" rx="1.6"></rect><path d="M1.8 6.2h12.4M6.4 6.2V13"></path>',
  },
  {
    prompt: "Rewrite the titles on slides 4 to 9",
    icon: '<path d="M2.5 11.5 11 3l2 2-8.5 8.5-2.6.6z"></path>',
  },
];

function renderEmptyState() {
  const starters = $("#empty-starters");
  if (starters.querySelector(".empty-starter")) return;
  EMPTY_STARTERS.forEach(({ prompt: suggestedPrompt, icon }) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "empty-starter";
    button.innerHTML = `<i><svg viewBox="0 0 16 16" aria-hidden="true">${icon}</svg></i><span></span>`;
    button.querySelector("span")!.textContent = suggestedPrompt;
    button.addEventListener("click", () => {
      const prompt = $("#prompt");
      prompt.value = suggestedPrompt;
      resizePrompt();
      syncComposerState();
      focusPrompt();
    });
    starters.append(button);
  });
}

function syncEmptyState() {
  const empty = messages.childElementCount === 0;
  $("#empty-state").classList.toggle("hidden", !empty);
  // The welcome screen replaces the transcript scroll region. Leaving the
  // empty region in the flex layout can push the composer below the viewport.
  conversationScroll.classList.toggle("hidden", empty);
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Some Office webviews expose the API but do not grant clipboard permission.
    }
  }
  const helper = document.createElement("textarea");
  helper.value = text;
  helper.setAttribute("readonly", "");
  helper.style.position = "fixed";
  helper.style.opacity = "0";
  document.body.append(helper);
  helper.select();
  const copied = document.execCommand("copy");
  helper.remove();
  if (!copied) throw new Error("Copy is unavailable");
}

function formatTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function createCopyButton(textProvider: () => string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "copy-message";
  button.setAttribute("aria-label", "Copy response");
  button.title = "Copy response";
  button.innerHTML = `
    <svg viewBox="0 0 18 18" aria-hidden="true">
      <path d="M5.5 12.5H4.8A1.8 1.8 0 0 1 3 10.7V4.8A1.8 1.8 0 0 1 4.8 3h5.9a1.8 1.8 0 0 1 1.8 1.8v.7" />
      <rect x="6.5" y="5.5" width="8.5" height="9.5" rx="1.8" />
    </svg>`;
  button.addEventListener("click", async () => {
    try {
      await copyText(textProvider());
      button.classList.add("copied");
      button.innerHTML = ICONS.tick;
      button.setAttribute("aria-label", "Response copied");
      button.title = "Copied";
      setTimeout(() => {
        button.classList.remove("copied");
        button.setAttribute("aria-label", "Copy response");
        button.title = "Copy response";
      }, 1400);
    } catch {
      button.title = "Could not copy response";
    }
  });
  return button;
}

function formatFileSize(bytes: number | undefined): string {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Names Deck Studio or the Sydani template gives shapes; they mean nothing to the user.
const INTERNAL_SHAPE_NAME = /^(SYDANI_AI_|DATA_TABLE$|SLOT_\d+$)/;
const SHAPE_TYPE_LABELS: Record<string, string> = {
  Table: "Table", Image: "Picture", Chart: "Chart", SmartArt: "SmartArt graphic", Group: "Group",
  TextBox: "Text box", Placeholder: "Text box", GeometricShape: "Shape", Line: "Line",
};

function selectedComponentAttachments(profile = state.presentation): UiAttachment[] {
  return (profile?.selectedShapes || []).map((shape) => {
    const text = String(shape.text || "").replace(/\s+/g, " ").trim();
    const shapeName = String(shape.name || "");
    const typeLabel = SHAPE_TYPE_LABELS[String(shape.type || "")] || "Selected component";
    const name = text || (shapeName && !INTERNAL_SHAPE_NAME.test(shapeName) ? shapeName : typeLabel);
    return {
      kind: "component" as const,
      name: name.slice(0, 240),
      shapeName: String(shape.name || ""),
      shapeId: String(shape.id || ""),
      slideNumber: profile?.selectedSlideNumber || null,
    };
  });
}

function attachmentDescription(attachment: UiAttachment): string {
  if (attachment.kind === "component") {
    return Number.isFinite(attachment.slideNumber)
      ? `Slide ${attachment.slideNumber}`
      : "Selected component";
  }
  return formatFileSize(attachment.size);
}

function renderAttachments(attachments: UiAttachment[]): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.className = "bubble-files";
  attachments.forEach((attachment) => {
    const metaText = attachmentDescription(attachment);
    const pill = document.createElement("span");
    pill.className = "bubble-file";
    if (attachment.kind === "component") pill.classList.add("bubble-component");
    pill.title = metaText ? `${attachment.name} · ${metaText}` : attachment.name;
    const icon = document.createElement("span");
    icon.className = "bubble-file-icon";
    icon.insertAdjacentHTML("afterbegin",
      ATTACHMENT_ICONS[attachment.kind === "component" ? "component" : "file"]);
    const name = document.createElement("span");
    name.className = "bubble-file-name";
    name.textContent = attachment.name;
    pill.append(icon, name);
    if (metaText) {
      const meta = document.createElement("span");
      meta.className = "bubble-file-size";
      meta.textContent = metaText;
      pill.append(meta);
    }
    wrap.append(pill);
  });
  return wrap;
}

/**
 * The transcript is a list of typed entries, not a list of messages.
 *
 * Every entry renders through `renderEntry`, live and on replay, so the two
 * cannot drift: what you see when it happens is what you see when you come
 * back. Pending changes are the one deliberate exception. They are an offer to
 * touch your presentation and they do not survive a close.
 */
function renderEntry(entry: ChatEntry): HTMLElement | null {
  switch (entry.kind) {
    case "message": return renderMessageEntry(entry);
    case "system": return renderSystemEntry(entry);
    case "storyline": return renderStorylineEntry(entry, touchEntry);
    case "poll": return renderPollEntry(entry, touchEntry);
    case "activity": return renderActivityEntry(entry);
    case "failure": return renderFailureEntry(entry);
    default: return null;
  }
}

/** Render an entry into the transcript, and keep it unless told not to. */
function appendEntry(entry: ChatEntry, persist = true): HTMLElement {
  const node = renderEntry(entry);
  if (!node) throw new Error(`Unsupported transcript entry: ${String(entry?.kind || "unknown")}`);
  messages.append(node);
  syncEmptyState();
  scrollConversationToEnd();
  if (persist) recordEntry(entry);
  return node;
}

function recordEntry(entry: ChatEntry): void {
  ensureEntryMetadata(entry);
  state.chatHistory.push(entry);
  state.chatHistory = retainedHistory(state.chatHistory);
  touchConversationTimestamp();
  syncActiveConversation();
  saveChatHistory();
  renderOpenConversationList();
}

/** Re-save after an entry already in the transcript has changed. */
function touchEntry(entry?: ChatEntry) {
  if (entry) {
    ensureEntryMetadata(entry);
    entry.updatedAt = new Date().toISOString();
  }
  touchConversationTimestamp();
  syncActiveConversation();
  saveChatHistory();
}

function renderMessageEntry(entry: DynamicRecord): HTMLElement {
  const role = entry.role === "user" ? "user" : "assistant";
  const article = document.createElement("article");
  article.className = `message ${role}`;
  if (entry.id) article.dataset.entryId = String(entry.id);
  const bubble = document.createElement("div");
  bubble.className = "bubble";
  if (role === "assistant") {
    bubble.classList.add("markdown");
    bubble.append(renderMarkdown(entry.text));
  } else {
    // Attachments are listed inside the bubble so the transcript keeps a
    // record of what the model was actually given, not just what was typed.
    const attachments = entry.attachments || [];
    if (attachments.length) bubble.append(renderAttachments(attachments));
    const body = document.createElement("span");
    body.className = "bubble-text";
    body.textContent = entry.text;
    bubble.append(body);
  }
  article.append(bubble);
  article.append(messageFooter(entry, role, bubble));
  return article;
}

function reconcileUserMessageAttachments(entry: ChatEntry, attachments: UiAttachment[]): void {
  const dynamicEntry = entry as DynamicRecord;
  const existing = Array.isArray(dynamicEntry.attachments) ? dynamicEntry.attachments : [];
  if (JSON.stringify(existing) === JSON.stringify(attachments)) return;
  dynamicEntry.attachments = attachments;
  touchEntry(entry);
  const visible = [...(messages.querySelectorAll(".message.user") as NodeListOf<HTMLElement>)]
    .find((node) => node.dataset.entryId === dynamicEntry.id);
  visible?.replaceWith(renderMessageEntry(dynamicEntry));
}

function messageFooter(entry: DynamicRecord, role: "user" | "assistant", bubble: HTMLElement): HTMLElement {
  // Time on every message; copy only on the assistant's, because a control for
  // words you typed yourself is a control for text you already have.
  const footer = document.createElement("div");
  footer.className = "message-footer";
  const time = document.createElement("span");
  time.className = "message-time";
  time.textContent = formatTime(entry.at ? new Date(entry.at) : new Date());
  footer.append(time);
  if (role === "assistant") {
    footer.append(createCopyButton(() => bubble.innerText.trim()));
  }
  return footer;
}

function addMessage(
  role: "user" | "assistant",
  text: unknown,
  persist = true,
  attachments: UiAttachment[] = [],
): ChatEntry {
  const entry: ChatEntry = {
    kind: "message",
    role,
    text: String(text),
    at: new Date().toISOString(),
    ...(attachments.length ? { attachments } : {}),
  };
  ensureEntryMetadata(entry);
  appendEntry(entry, persist);
  return entry;
}

/** Record a live storyline and render its progress card. */
function addStoryline(
  slides: DynamicRecord[],
  built = 0,
  existingDesigns: string[] = [],
): StorylineHandle {
  const entry: StorylineHandle["entry"] = {
    kind: "storyline",
    slides,
    built: Math.min(Math.max(built, 0), slides.length),
    designs: slides.map((slide, index) => (
      existingDesigns[index] || String(slide.slideTypeLabel || "")
    )),
    live: true,
  };
  const element = appendEntry(entry);
  return { entry, element, list: element.querySelector("ol")! };
}

/** Preserve an earlier revision in storage and the audit-linked transcript,
 * while removing its stale card from the reader's chronological view. */
function supersedeStoryline(storyline: StorylineHandle): void {
  storyline.entry.live = false;
  storyline.entry.superseded = true;
  storyline.element.remove();
  touchEntry(storyline.entry as ChatEntry);
}

/** Advance the storyline as slides land in the draft. */
function markStorylineProgress(
  storyline: StorylineHandle | null,
  built: number,
  design = "",
  at = built,
): void {
  if (!storyline) return;
  setStorylineDesign(storyline, at, design);
  storyline.entry.built = built;
  applyStorylineState(storyline.list, built, true);
  touchEntry(storyline.entry as ChatEntry);
}

function setStorylineDesign(
  storyline: StorylineHandle | null,
  at: number,
  design: string,
): void {
  if (!storyline || !design || at <= 0) return;
  storyline.entry.designs[at - 1] = design;
  const label = storyline.list.children.item(at - 1)
    ?.querySelector<HTMLElement>(".storyline-title small");
  if (label) {
    label.textContent = design;
    delete label.dataset.working;
  }
}

/** Nothing is in progress once the turn ends, so the dots come off. */
function settleStoryline(storyline: StorylineHandle | null): void {
  if (!storyline) return;
  storyline.entry.live = false;
  applyStorylineState(storyline.list, storyline.entry.built, false);
  touchEntry(storyline.entry as ChatEntry);
}

/** The slide type for a design the model just used, for the storyline. */
function designLabelOf(activity: DynamicRecord): string {
  return String(activity.result?.slideTypeLabel || "").trim();
}

function designSearchLabel(activity: DynamicRecord): string {
  const args = activity.arguments || {};
  const needs = Array.isArray(args.needs) ? args.needs : [];
  if (needs.length === 1) {
    return String(needs[0]?.query || "").trim() || "the requested content";
  }
  return needs.length ? `${needs.length} content structures` : "the requested content";
}

function completedDraftActivity(activity: DynamicRecord): { text: string } {
  const at = activity.result?.at;
  if (activity.result?.error) return { text: `Could not ${activity.name.replaceAll("_", " ")}` };
  switch (activity.name) {
    case "find_designs":
      return { text: `Searched designs for ${designSearchLabel(activity)}` };
    case "inspect_design": {
      const category = String(activity.result?.slideTypeLabel || "").trim().toLowerCase();
      return { text: category ? `Reviewed ${category} category` : "Reviewed a slide category" };
    }
    case "add_slides": {
      const added = Array.isArray(activity.result?.added) ? activity.result.added : [];
      if (added.length === 1) return { text: `Added slide ${added[0]?.at}` };
      return { text: `Added ${added.length} slides` };
    }
    case "edit_slide":
      return { text: `Edited slide ${at}` };
    case "replace_slide":
      return { text: `Rebuilt slide ${at}` };
    case "move_slide":
      return { text: `Moved slide ${activity.result?.from} to ${activity.result?.to}` };
    case "delete_slide":
      return { text: `Deleted slide ${activity.arguments?.at}` };
    case "set_slide_artwork_visibility":
      return { text: activity.result?.visible
        ? `Restored artwork on slide ${at}`
        : `Removed artwork from slide ${at}` };
    case "read_draft":
      return { text: "Read the draft" };
    case "check_slides": {
      const count = activity.result?.findings?.length || 0;
      return { text: count
        ? `Found ${count} issue${count === 1 ? "" : "s"}`
        : "Checked the deck" };
    }
    case "preview_slides": {
      const shown = activity.result?.rendered || [];
      return { text: shown.length === 1
        ? `Reviewed slide ${shown[0]}`
        : `Reviewed ${shown.length} slides` };
    }
    case "edit_presentation": {
      const queued = Number(activity.result?.queued) || 0;
      if (activity.result?.withdrawn !== undefined) {
        // The agent restated its whole queue, usually to take a mistake back.
        return { text: queued
          ? `Revised the changes to ${queued}`
          : "Withdrew the proposed changes" };
      }
      return { text: `Prepared ${queued} change${queued === 1 ? "" : "s"}` };
    }
    default:
      return { text: `Completed ${activity.name.replaceAll("_", " ")}` };
  }
}


function renderSystemEntry(entry: DynamicRecord): HTMLElement {
  const line = document.createElement("div");
  line.className = `system ${entry.tone || "note"}`;
  line.insertAdjacentHTML("afterbegin",
    ICONS[(entry.icon || (entry.tone === "done" ? "done" : "canvas")) as keyof typeof ICONS] || ICONS.canvas);
  const label = document.createElement("span");
  label.textContent = entry.text;
  line.append(label);
  return line;
}

function addSystemMessage(
  text: unknown,
  tone: "note" | "done" = "note",
  icon: keyof typeof ICONS | null = null,
) {
  return appendEntry({ kind: "system", text: String(text), tone, ...(icon ? { icon } : {}) });
}

/**
 * What happened to proposed changes after the agent's turn ended. The agent
 * never sees Apply or Discard happen, so these are handed to it with the next
 * message: without them it describes the deck as it was when it last spoke.
 */
function addOutcome(
  text: string,
  tone: "note" | "done" = "note",
  icon: keyof typeof ICONS | null = null,
) {
  return appendEntry({
    kind: "system", text, tone, outcome: true, delivered: false, ...(icon ? { icon } : {}),
  });
}

/**
 * Outcomes the agent has not yet been told about, oldest first. Sending a
 * message is not enough to count: a send can stop before it reaches the
 * server, so an outcome is delivered only once a turn carrying it does.
 */
function undeliveredOutcomes(): ChatEntry[] {
  return state.chatHistory.filter((entry) => (
    entry.kind === "system" && entry.outcome === true && (entry as DynamicRecord).delivered === false
  ));
}

function markOutcomesDelivered(outcomes: ChatEntry[]): void {
  outcomes.forEach((entry) => {
    (entry as DynamicRecord).delivered = true;
    touchEntry(entry);
  });
}

function renderFailureEntry(entry: DynamicRecord): HTMLElement {
  const card = document.createElement("section");
  card.className = "failure";
  const head = document.createElement("div");
  head.className = "failure-head";
  head.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.4"></circle><path d="M8 4.8v3.6M8 11v.1"></path></svg>';
  const copy = document.createElement("div");
  const title = document.createElement("div");
  title.className = "failure-title";
  title.textContent = entry.title;
  const body = document.createElement("p");
  body.className = "failure-body";
  body.textContent = entry.body;
  copy.append(title, body);
  head.append(copy);
  card.append(head);
  // Only a card whose request is still known can be retried: one replayed
  // from storage, or from before a chat switch, has nothing to send again.
  if (entry.retryable && state.failedRequests.has(entry.id)) {
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = "Try again";
    retry.addEventListener("click", async () => {
      const failedRequest = state.failedRequests.get(entry.id);
      // Checked before the card goes, or a click during Apply would remove
      // the card and then send nothing.
      if (!failedRequest || !state.ready || state.busy || state.applying) return;
      card.remove();
      state.chatHistory = state.chatHistory.filter((item) => item !== entry);
      state.files = failedRequest.files;
      state.failedRequests.delete(entry.id);
      syncActiveConversation();
      saveChatHistory();
      renderFiles();
      await sendPrompt(failedRequest.message, {
        skipUserMessage: true,
        usageEntry: failedRequest.entry,
      });
    });
    card.append(retry);
  }
  return card;
}

function failureEntry(error: unknown): ChatEntry {
  const sessionError = error as SessionError;
  if (sessionError.kind === "configuration") {
    return {
      kind: "failure",
      title: "Deck Studio’s connection address does not match",
      body: errorMessage(
        error,
        "The server does not allow this add-in address. Check Deck Studio’s connection settings.",
      ),
      retryable: true,
    };
  }
  if (error instanceof DeadlineError) {
    return {
      kind: "failure",
      title: "PowerPoint is not responding",
      body: error.message,
      retryable: true,
    };
  }
  const connectionProblem = /connection|network|disconnect|websocket|failed to fetch/i
    .test(errorMessage(error, ""));
  const kept = " Any work already completed is kept.";
  return {
    kind: "failure",
    title: connectionProblem ? "Lost the connection to Deck Studio" : "The request could not be completed",
    body: connectionProblem
      ? `The reply did not arrive.${kept}`
      : `${errorMessage(error, "Deck Studio could not complete the request.")}${kept}`,
    retryable: true,
  };
}

/** Show a failure card that retries the request that failed, and no other. */
function addFailure(error: unknown, request: FailedRequest) {
  const entry = failureEntry(error);
  ensureEntryMetadata(entry);
  state.failedRequests.set(entry.id!, request);
  return appendEntry(entry);
}

function addLoadingIndicator(initialText = "Working") {
  const status = document.createElement("div");
  status.className = "loading-indicator";
  status.setAttribute("role", "status");
  status.setAttribute("aria-label", "Working on your request");

  const label = document.createElement("span");
  label.className = "loading-label";
  label.textContent = initialText;
  const dots = document.createElement("span");
  dots.className = "loading-dots";
  dots.setAttribute("aria-hidden", "true");
  for (let index = 0; index < 3; index += 1) {
    const dot = document.createElement("span");
    dot.className = "loading-dot";
    dots.append(dot);
  }

  status.append(label, dots);
  messages.append(status);
  scrollConversationToEnd();
  return status;
}

function updateLoadingIndicator(status: HTMLElement | null, text: string): void {
  const label = status?.querySelector(".loading-label");
  if (!status || !label) return;
  label.textContent = String(text || "Working").replace(/\.{3}$/, "").trim();
  status.setAttribute("aria-label", label.textContent);
}

function completeLoadingIndicator(status: HTMLElement | null, text: string): void {
  if (!status?.querySelector(".loading-label")) return;
  updateLoadingIndicator(status, text || "Completed");
  status.classList.add("completed");
}

function renderActivityEntry(entry: DynamicRecord): HTMLElement {
  const group = document.createElement("div");
  group.className = "activity";
  (entry.steps || []).forEach((step: DynamicRecord) => group.append(activityStepNode(step)));
  return group;
}

function activityStepNode(step: DynamicRecord): HTMLElement {
  const item = document.createElement("div");
  item.className = "activity-step";
  if (step.failed) item.classList.add("failed");
  // The icon markup comes from presets.js, never from a tool result.
  item.insertAdjacentHTML("afterbegin", toolIcon(step.name));
  const label = document.createElement("span");
  label.className = "activity-step-label";
  const text = document.createElement("span");
  text.textContent = step.text;
  label.append(text);
  item.append(label);
  if (step.pending) item.append(createActivityDots());
  return item;
}

function addActivityGroup(): ActivityGroupHandle {
  // A flat trail rather than a collapsible group. The steps are the product's
  // one window onto what it is doing; folding them away by default hid the
  // thing worth watching.
  const entry: DynamicRecord = { kind: "activity", steps: [] };
  const element = appendEntry(entry as ChatEntry);
  return { entry, element, list: element, count: 0 };
}

function addActivityStep(
  group: ActivityGroupHandle,
  name: string,
  text: string,
  pending = false,
): ActivityStepHandle {
  const step = { name, text, pending };
  group.entry.steps.push(step);
  const item = activityStepNode(step);
  item.dataset.step = String(group.entry.steps.length - 1);
  group.list.append(item);
  group.count += 1;
  touchEntry(group.entry as ChatEntry);
  scrollConversationToEnd();
  return { item, step, entry: group.entry as ChatEntry };
}

function completeActivityStep(handle: ActivityStepHandle | null, text: string, failed = false): void {
  if (!handle) return;
  const { item, step, entry } = handle;
  step.text = text;
  step.failed = failed;
  step.pending = false;
  item.querySelector(".activity-step-label > span")!.textContent = text;
  item.querySelector(".loading-dots")?.remove();
  item.classList.toggle("failed", failed);
  touchEntry(entry);
}

function stopActivityStep(handle: ActivityStepHandle | null): void {
  if (!handle) return;
  const action = String(handle.step.text || "working")
    .replace(/\.{3}$|…$/, "")
    .toLowerCase();
  completeActivityStep(handle, `Stopped while ${action}`);
}

function completeActivityGroup(group: ActivityGroupHandle | null): void {
  // Keep the specific target visible while work is live. Once the turn is
  // complete, consecutive edits of the same kind become one past-tense row so
  // a batch of repairs does not overwhelm the transcript.
  if (!group) return;
  group.entry.steps = completedActivityRun(group.entry.steps || []);
  group.element.replaceChildren(...group.entry.steps.map(activityStepNode));
  touchEntry(group.entry as ChatEntry);
}

function syncComposerState() {
  const prompt = $("#prompt");
  const waiting = state.awaitingAnswer;
  const disabled = !state.ready || waiting || state.applying;
  prompt.disabled = disabled;
  $("#files").disabled = disabled;
  $("#file-label").classList.toggle("disabled", disabled);
  $("#file-label").setAttribute("aria-disabled", String(disabled));
  $(".composer-shell").classList.toggle("disabled", disabled);
  prompt.placeholder = waiting
    ? "Answer above to continue…"
    : state.pendingChanges.length
      ? "Ask for a change…"
      : "Build with Sydani AI…";
  const send = $("#send-button");
  const hasDraft = Boolean(prompt.value.trim());
  // A draft typed before a poll appeared cannot be sent until it is answered,
  // so it must not take the place of the only way out of the turn.
  const showStop = state.busy && (waiting || !hasDraft);
  send.classList.toggle("stopping", showStop);
  send.setAttribute("aria-label", showStop
    ? state.stopping ? "Stopping" : "Stop generating"
    : state.busy ? "Wait for the current response" : "Send message");
  send.disabled = !state.ready || state.applying || (state.busy
    ? state.stopping || !showStop
    : waiting || !hasDraft);
}

function setBusy(busy: boolean): void {
  state.busy = busy;
  if (!busy) state.stopping = false;
  const locked = busy || state.applying;
  $("#chat-history-button").disabled = locked || !state.ready;
  $("#new-chat-button").disabled = locked || !state.ready;
  $("#sign-out-button").disabled = locked;
  $("#discard-changes-button").disabled = locked;
  $("#apply-changes-button").disabled = locked || state.applyNeedsReview || state.pendingChanges.length === 0;
  syncComposerState();
}

function stopActiveTurn(): void {
  if (!state.busy || state.stopping || !activeTurnController) return;
  state.stopping = true;
  syncComposerState();
  activeTurnController.abort();
}

function renderFiles() {
  const list = $("#file-list");
  list.replaceChildren();
  selectedComponentAttachments().forEach((component) => {
    const pill = document.createElement("span");
    pill.className = "file-pill component-pill";
    pill.title = `${component.name} · ${attachmentDescription(component)}`;
    pill.insertAdjacentHTML("afterbegin", ATTACHMENT_ICONS.component);
    const name = document.createElement("span");
    name.className = "file-pill-name";
    name.textContent = component.name;
    const meta = document.createElement("span");
    meta.className = "file-pill-meta";
    meta.textContent = attachmentDescription(component);
    pill.append(name, meta);
    list.append(pill);
  });
  state.files.forEach((file, index) => {
    const pill = document.createElement("span");
    pill.className = "file-pill";
    pill.title = `${file.name} · ${Math.max(1, Math.round(file.size / 1024))} KB`;
    pill.insertAdjacentHTML("afterbegin", ATTACHMENT_ICONS.file);
    const name = document.createElement("span");
    name.className = "file-pill-name";
    name.textContent = file.name;
    const size = document.createElement("span");
    size.className = "file-pill-meta";
    size.textContent = formatFileSize(file.size);
    const remove = document.createElement("button");
    remove.className = "file-remove";
    remove.type = "button";
    remove.setAttribute("aria-label", `Remove ${file.name}`);
    remove.insertAdjacentHTML("afterbegin", ATTACHMENT_ICONS.close);
    remove.addEventListener("click", () => {
      state.files.splice(index, 1);
      renderFiles();
      // Removing the pill destroys the button that had focus, which would
      // otherwise drop focus to the document body.
      focusPrompt();
    });
    pill.append(name, size, remove);
    list.append(pill);
  });
  syncComposerState();
}

function resizePrompt() {
  const prompt = $("#prompt");
  prompt.style.height = "auto";
  const height = Math.min(prompt.scrollHeight, 88);
  prompt.style.height = `${height}px`;
  prompt.style.overflowY = prompt.scrollHeight > 88 ? "auto" : "hidden";
}

function focusPrompt() {
  const prompt = $("#prompt");
  if (prompt.disabled) return;
  try {
    prompt.focus({ preventScroll: true });
  } catch {
    prompt.focus();
  }
}

function fileToPayload(file: File): Promise<{ name: string; type: string; data: string | undefined }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve({
      name: file.name,
      type: file.type || "application/octet-stream",
      data: String(reader.result).split(",", 2)[1],
    });
    reader.readAsDataURL(file);
  });
}

/**
 * A populated deck of another shape. Sydani designs are 4:3, so adding them
 * changes the size of the whole presentation and reflows what is already there.
 * That is the user's decision, made on the review card. An empty deck has
 * nothing to reflow and is converted without asking.
 */
function canvasConversionNeeded(profile: PresentationProfile | null): boolean {
  return Boolean(profile?.pageSetupSupported && profile.isFourByThree === false && !profile.isProbablyBlank);
}

function inches(points: unknown): string {
  return (Number(points) / 72).toFixed(2).replace(/\.?0+$/, "");
}

function presentationContext(outcomes: string[] = []) {
  if (!state.presentation) return null;
  const {
    width, height, slideCount, selectedSlideId,
    selectedSlideNumber, selectedShapes,
    isProbablyBlank, capabilities,
  } = state.presentation;
  return {
    // Dimensions are document state used to interpret PowerPoint's absolute
    // shape geometry, not a design choice: generated slides are always 4:3.
    width,
    height,
    slideCount,
    selectedSlideId,
    selectedSlideNumber,
    selectedShapes,
    capabilities,
    // The situation in plain words, rather than a flag to interpret. Handed
    // `isProbablyBlank: true`, the model read an empty deck as a decision to
    // put to the user and polled on whether to keep or delete the blank slide,
    // a question whose answer applyChanges() discards: it removes that slide
    // itself once the deck lands.
    status: isProbablyBlank
      ? "This presentation is empty and ready for a new deck to be built into "
        + "it. Its single blank slide is removed automatically once the new "
        + "deck is added, so it needs no handling."
      : `This presentation already has ${slideCount} slide(s) of existing content.`,
    ...(canvasConversionNeeded(state.presentation) ? {
      slideSize: `The slides are ${inches(width)} x ${inches(height)} inches, not the Sydani `
        + "4:3 canvas. Adding Sydani slides changes the whole presentation to 10 x 7.5 inches "
        + "and reflows every existing slide. The user approves that when they apply.",
    } : {}),
    // What the user did with your proposals after your last reply. Without it
    // you describe the deck as it was when you last spoke.
    ...(outcomes.length ? { sinceYourLastReply: outcomes } : {}),
  };
}

// How long PowerPoint may take to describe the open presentation before a
// message is sent, and to answer one read during a turn. Generous for a large
// deck, short enough that nobody stares at a spinner that will never finish.
const PRESENTATION_CHECK_MS = 15_000;
const POWERPOINT_READ_MS = 60_000;
// A copy exports the whole slide, pictures included, and inserts it again.
const COPY_SLIDE_MS = 120_000;
// When a presentation read is sent a second time; well inside the check's limit.
const READ_HEDGE_MS = 5_000;
// How long the trail may sit still before the pane says the model is thinking.
// Back-to-back tool calls usually land within this, so the row does not flash.
const THINKING_DELAY_MS = 2_000;
const POWERPOINT_SILENT = "PowerPoint did not respond, so your message was not sent. "
  + "Try again in a moment. If it keeps happening, close and reopen Deck Studio.";

// Reads during a turn are sent again when PowerPoint has not answered by
// resendMs: on the web it drops requests, and a dropped render once cost a
// minute. Renders take longer than reads, so they wait longer before a resend.
const POWERPOINT_READ_TOOLS = Object.freeze({
  read_presentation: { run: readPresentation, resendMs: 15_000 },
  render_presentation: { run: renderPresentation, resendMs: 25_000 },
  search_presentation: { run: searchPresentation, resendMs: 15_000 },
});

async function runAgentSession(
  request: DynamicRecord,
  onActivity: (activity: DynamicRecord) => void,
  onAsk: (questions: Question[]) => Promise<PollAnswer[]>,
  signal?: AbortSignal,
  onReached: () => void = () => {},
): Promise<DynamicRecord> {
  if (microsoftSignIn) {
    let session: Response;
    try {
      session = await sessionFetch("/api/auth/session", { signal });
    } catch (error) {
      // Stop pressed during this check aborts the fetch before anything was
      // sent, which is a stop and not a failure to report.
      if (signal?.aborted) throw new StoppedError();
      throw error;
    }
    if (!session.ok) throw new Error("Sign in again to continue this conversation.");
  }
  return new Promise<DynamicRecord>((resolve, reject) => {
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${scheme}//${window.location.host}/ws/session`);
    let settled = false;
    let reached = false;
    let stopping = Boolean(signal?.aborted);

    const finish = (callback: (value: any) => void, value: any): void => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", requestStop);
      try { socket.close(); } catch { /* already closed */ }
      callback(value);
    };

    const requestStop = (): void => {
      stopping = true;
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "cancel" }));
      }
    };
    signal?.addEventListener("abort", requestStop, { once: true });

    socket.addEventListener("open", () => {
      socket.send(JSON.stringify({ type: "start", ...request }));
      if (stopping) socket.send(JSON.stringify({ type: "cancel" }));
    });
    socket.addEventListener("message", async (event) => {
      // Any reply means the server has the request, and with it everything
      // the request carried.
      if (!reached) {
        reached = true;
        onReached();
      }
      let message: DynamicRecord;
      try {
        message = JSON.parse(String(event.data)) as DynamicRecord;
      } catch {
        finish(reject, new Error("Deck Studio received a reply it could not read. Please try again."));
        return;
      }

      if (message.type === "plan") {
        onActivity({
          phase: "plan",
          slides: message.slides || [],
          built: message.built || 0,
          designs: message.designs || [],
        });
        return;
      }
      if (message.type === "assistant_message") {
        onActivity({
          phase: "assistant_message",
          message: String(message.message || ""),
        });
        return;
      }
      if (message.type === "plan_start") {
        onActivity({ phase: "plan_start" });
        return;
      }
      if (message.type === "activity_round_complete") {
        onActivity({ phase: "activity_round_complete" });
        return;
      }
      if (message.type === "activity") {
        // Work on the server-side draft. There is nothing for the client to
        // run; this is the model narrating what it is building.
        onActivity({
          phase: "draft", name: message.name,
          arguments: message.arguments || {}, result: message.result || {},
        });
        return;
      }
      if (message.type === "tool_call" && message.name === "ask_user") {
        // The poll is a tool now, so the answers come back inside this turn
        // rather than starting another one.
        try {
          const answers = await onAsk(message.arguments?.questions || []);
          socket.send(JSON.stringify({
            type: "tool_result", id: message.id, result: { answers },
          }));
        } catch (error) {
          if (signal?.aborted) return;
          socket.send(JSON.stringify({
            type: "tool_error", id: message.id,
            message: errorMessage(error, "The user did not answer."),
          }));
        }
        return;
      }
      if (message.type === "tool_call") {
        const tool = POWERPOINT_READ_TOOLS[message.name as keyof typeof POWERPOINT_READ_TOOLS] as
          | { run: (arguments_: DynamicRecord) => Promise<unknown>; resendMs: number }
          | undefined;
        if (!tool) {
          socket.send(JSON.stringify({
            type: "tool_error",
            id: message.id,
            message: `Unsupported PowerPoint read tool: ${message.name}`,
          }));
          return;
        }
        onActivity({ phase: "start", name: message.name, arguments: message.arguments || {} });
        try {
          const result = await withinTime(
            hedged(() => tool.run(message.arguments || {}), tool.resendMs), POWERPOINT_READ_MS,
            `PowerPoint did not answer ${message.name} within ${POWERPOINT_READ_MS / 1000} seconds. `
              + "Tell the user PowerPoint stopped responding and that reopening Deck Studio usually fixes it.",
          );
          if (signal?.aborted) return;
          onActivity({
            phase: "complete", name: message.name,
            arguments: message.arguments || {}, result,
          });
          socket.send(JSON.stringify({ type: "tool_result", id: message.id, result }));
        } catch (error) {
          if (signal?.aborted) return;
          if (error instanceof DeadlineError) {
            void auditClientEvent("powerpoint.unresponsive", { phase: message.name, ...powerPointActivity() }, null);
          }
          onActivity({
            phase: "error", name: message.name,
            arguments: message.arguments || {}, error,
          });
          socket.send(JSON.stringify({
            type: "tool_error",
            id: message.id,
            message: errorMessage(error, "The PowerPoint read failed."),
          }));
        }
        return;
      }
      if (message.type === "done") {
        finish(resolve, message.response);
        return;
      }
      if (message.type === "interrupted") {
        finish(resolve, { ...(message.response || {}), interrupted: true });
        return;
      }
      if (message.type === "error") {
        const error = new Error(String(message.message || "The AI request failed.")) as SessionError;
        error.resetResponseId = Boolean(message.resetResponseId);
        if (typeof message.responseId === "string") error.responseId = message.responseId;
        error.auditTurnId = typeof message.auditTurnId === "string" ? message.auditTurnId : undefined;
        finish(reject, error);
      }
    });
    socket.addEventListener("error", () => {
      // Browsers emit `error` before `close`, but only the close event carries
      // the server's useful 4401/4403 reason. Let `close` settle the request.
    });
    socket.addEventListener("close", (event) => {
      if (event.code === 4401) {
        showSignedOut();
        if (!settled) finish(reject, new Error("Your Deck Studio session expired. Sign in again."));
        return;
      }
      if (event.code === 4403) {
        const error = new Error(
          `The server does not allow ${window.location.origin}. `
          + "Check DECK_STUDIO_ORIGIN and restart Deck Studio.",
        ) as SessionError;
        error.kind = "configuration";
        if (!settled) finish(reject, error);
        return;
      }
      if (!settled) {
        finish(
          reject,
          new Error(stopping ? "The request was stopped." : "The connection to Deck Studio was lost."),
        );
      }
    });
  });
}

function joinedSlideNumbers(numbers: Array<number | undefined>): string | null {
  const unique = [...new Set(numbers)].filter(
    (value): value is number => typeof value === "number" && Number.isFinite(value));
  if (!unique.length) return null;
  if (unique.length === 1) return `slide ${unique[0]}`;
  if (unique.length === 2) return `slides ${unique[0]} and ${unique[1]}`;
  return `slides ${unique.slice(0, -1).join(", ")} and ${unique.at(-1)}`;
}

function activityTarget(args: DynamicRecord, slideNumberById: Map<string, number>): string {
  const target = args?.target || {};
  if (target.kind === "selection") {
    return state.presentation?.selectedSlideNumber
      ? `slide ${state.presentation.selectedSlideNumber}`
      : "the selected slide";
  }
  if (target.kind === "slide_numbers") {
    return joinedSlideNumbers(target.slideNumbers || []) || "the requested slides";
  }
  if (target.kind === "slide_ids") {
    const numbers = (target.slideIds || []).map((id: string) => slideNumberById.get(id));
    return joinedSlideNumbers(numbers)
      || ((target.slideIds || []).length === 1 ? "the requested slide" : "the requested slides");
  }
  return args?.cursor ? "more of the presentation" : "the presentation";
}

function ongoingActivity(name: string, args: DynamicRecord, slideNumberById: Map<string, number>): string {
  const target = activityTarget(args, slideNumberById);
  if (name === "read_presentation") return `Reading ${target}`;
  if (name === "render_presentation") return `Reviewing ${target}`;
  const query = String(args?.query || "").trim();
  return query
    ? `Searching ${target} for “${query.length > 48 ? `${query.slice(0, 47)}…` : query}”`
    : `Searching ${target}`;
}

function completedActivity(record: DynamicRecord, slideNumberById: Map<string, number>): string {
  const target = activityTarget(record.arguments, slideNumberById);
  if (record.name === "read_presentation") return `Read ${target}`;
  if (record.name === "render_presentation") return `Reviewed ${target}`;
  return `Searched ${target}`;
}

function rememberSlideNumbers(result: DynamicRecord, slideNumberById: Map<string, number>): void {
  [result?.slides, result?.images, result?.matches].forEach((items) => {
    (items || []).forEach((item: DynamicRecord) => {
      if (item?.slideId && Number.isFinite(item.slideNumber)) {
        slideNumberById.set(item.slideId, item.slideNumber);
      }
    });
  });
}

async function refreshPresentationProfile(options: { selectedShapes?: boolean } = {}) {
  const profile = await hedged(() => inspectPresentation(options), READ_HEDGE_MS);
  state.presentation = profile;
  renderFiles();
  return profile;
}

function schedulePresentationRefresh(): void {
  if (contextRefreshTimer !== null) clearTimeout(contextRefreshTimer);
  contextRefreshTimer = setTimeout(() => {
    // Refreshes run one at a time, so one PowerPoint never answers would
    // otherwise hold every later selection change behind it.
    contextRefreshQueue = contextRefreshQueue
      .then(() => withinTime(refreshPresentationProfile(), PRESENTATION_CHECK_MS, "PowerPoint is slow to respond."))
      .catch(() => null);
  }, 150);
}

function registerAutomaticContextRefresh() {
  if (contextRefreshRegistered) return;
  contextRefreshRegistered = true;
  if (Office.context.requirements.isSetSupported("DocumentEvents", "1.1")) {
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      schedulePresentationRefresh,
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.warn("Could not subscribe to PowerPoint selection changes", result.error);
        }
      },
    );
  }
  window.addEventListener("focus", schedulePresentationRefresh);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") schedulePresentationRefresh();
  });
}

function slideThumbnailNode(change: ClientChange, slideNumber: number | null = null): HTMLSpanElement {
  const thumb = document.createElement("span");
  thumb.className = "slide-thumb";
  // The drawing is chosen by section, from presets.js. Never from a payload.
  thumb.insertAdjacentHTML("afterbegin", slideThumbnail(change.section || ""));
  if (slideNumber != null) {
    const badge = document.createElement("span");
    badge.className = "slide-thumb-number";
    badge.textContent = String(slideNumber);
    thumb.append(badge);
  }
  return thumb;
}

interface EditThumbnailRow { change: ClientChange; slideNumber: number | null; thumb: HTMLSpanElement }

// Pictures of the slides on the card are a nicety; ones PowerPoint takes
// longer than this to draw are not shown.
const THUMBNAIL_MS = 20_000;

async function loadEditThumbnails(rows: EditThumbnailRow[]): Promise<void> {
  const slideIds = [...new Set(rows.map(({ change }) => change.slideId)
    .filter((id): id is string => typeof id === "string" && id.length > 0 && !isCopyId(id)))];
  if (!slideIds.length) return;
  try {
    const rendered = await withinTime(renderPresentation({
      target: { kind: "slide_ids", slideIds },
      quality: "low",
    }), THUMBNAIL_MS, "");
    const imageById = new Map((rendered.images || []).map((image) => [image.slideId, image]));
    rows.forEach(({ change, thumb, slideNumber }) => {
      const image = change.slideId ? imageById.get(change.slideId) : undefined;
      if (!image?.data || !thumb.isConnected) return;
      const preview = document.createElement("img");
      preview.src = `data:${image.mimeType || "image/png"};base64,${image.data}`;
      preview.alt = "";
      preview.loading = "lazy";
      thumb.replaceChildren(preview);
      if (slideNumber != null) {
        const badge = document.createElement("span");
        badge.className = "slide-thumb-number";
        badge.textContent = String(slideNumber);
        thumb.append(badge);
      }
    });
  } catch {
    // Hosts below PowerPoint API 1.8, and slides drawn too slowly, keep the
    // restrained schematic fallback.
  }
}

function changeGroupHeading(label: string): HTMLLIElement {
  const heading = document.createElement("li");
  heading.className = "change-group";
  const text = document.createElement("span");
  text.textContent = label;
  const rule = document.createElement("i");
  heading.append(text, rule);
  return heading;
}

function changeRow(change: ClientChange, slideNumber: number | null): { row: HTMLLIElement; thumb: HTMLSpanElement } {
  const row = document.createElement("li");
  row.className = "change-item";
  if (change.type !== "add_slide") row.classList.add("edit");
  const thumb = slideThumbnailNode(change, slideNumber);
  row.append(thumb);
  const body = document.createElement("span");
  body.className = "change-item-body";
  const title = document.createElement("span");
  title.className = "change-item-title";
  title.textContent = change.description || "";
  body.append(title);
  // The section's name, never the design id: "Cover", not "cover_slide_01".
  const design = change.copyOf ? "Copy of one of your slides"
    : change.design || (change.type === "add_slide" ? "" : changeSummary(change));
  if (design) {
    const sub = document.createElement("span");
    sub.className = "change-item-design";
    sub.textContent = design;
    body.append(sub);
  }
  row.append(body);
  return { row, thumb };
}

function canvasConversionRow(profile: PresentationProfile | null): HTMLLIElement {
  const row = document.createElement("li");
  row.className = "change-item edit canvas-change";
  const thumb = document.createElement("span");
  thumb.className = "slide-thumb";
  thumb.innerHTML = '<svg viewBox="0 0 56 42" aria-hidden="true">'
    + '<rect x="4" y="9.5" width="48" height="23" rx="1.5" class="canvas-from"></rect>'
    + '<rect x="12.5" y="5" width="31" height="32" rx="1.5" class="canvas-to"></rect></svg>';
  const body = document.createElement("span");
  body.className = "change-item-body";
  const title = document.createElement("span");
  title.className = "change-item-title";
  title.textContent = "Change the presentation to 4:3 so the Sydani slides fit";
  const sub = document.createElement("span");
  sub.className = "change-item-design";
  const count = Number(profile?.slideCount) || 0;
  sub.textContent = `Your ${count} existing slide${count === 1 ? "" : "s"} will reflow to 10 x 7.5 in`;
  body.append(title, sub);
  row.append(thumb, body);
  return row;
}

function isCopyId(id: unknown): boolean {
  return typeof id === "string" && /^copy-\d{1,3}$/.test(id);
}

function isSydaniSlide(change: ClientChange): boolean {
  return change.type === "add_slide" && !change.copyOf;
}

/** "slide 4", "slides 20 to 24", "slides 3 and 20 to 24". */
function slideList(numbers: number[]): string {
  const sorted = [...numbers].sort((a, b) => a - b);
  const ranges: string[] = [];
  for (let start = 0; start < sorted.length;) {
    let end = start;
    while (end + 1 < sorted.length && sorted[end + 1] === sorted[end]! + 1) end += 1;
    // Two neighbours read better as a pair than as a range.
    if (end - start >= 2) ranges.push(`${sorted[start]} to ${sorted[end]}`);
    else for (let index = start; index <= end; index += 1) ranges.push(`${sorted[index]}`);
    start = end + 1;
  }
  const text = ranges.length > 1 ? `${ranges.slice(0, -1).join(", ")} and ${ranges.at(-1)}` : ranges[0] || "";
  return `${sorted.length === 1 ? "slide" : "slides"} ${text}`;
}

/** Name a copy's source and place by slide number once the deck is read. */
async function labelCopies(rows: Array<{ change: ClientChange; label: HTMLElement }>): Promise<void> {
  try {
    const order = await slideOrder();
    const number = (id?: string | null) => (id ? order.indexOf(id) + 1 : 0);
    rows.forEach(({ change, label }) => {
      const source = number(change.copyOf);
      if (!source || !label.isConnected) return;
      const after = number(change.afterSlideId);
      label.textContent = `Copy of slide ${source}${after ? `, placed after slide ${after}` : ""}`;
    });
  } catch {
    // The generic label stays when PowerPoint cannot list the slides.
  }
}

/**
 * Changes that were to follow `from` now follow `to`: the slide just placed
 * there, or the real slide a copy id stood for. Rewritten in the pending list
 * so a retry after a failure places them where the first attempt would have.
 */
function followOn(changes: ClientChange[], from: string, to: string): ClientChange[] {
  return changes.map((change) => {
    const anchor = change.afterSlideId || (change.copyOf ? change.copyOf : null);
    return anchor === from ? { ...change, afterSlideId: to } : change;
  });
}

/** Point an edit written against a copy's source at the copy itself. */
function retarget(change: ClientChange, copies: Map<string, SlideCopy>): ClientChange {
  const copy = change.slideId ? copies.get(change.slideId) : undefined;
  const shapeIds = copy?.shapeIds;
  if (!copy || !shapeIds) return change;
  const path = change.groupPath || [];
  const mapped = (index: number) => shapeIds.get(path.slice(0, index + 1).join("/"));
  const groupPath = path.map((_, index) => mapped(index));
  const shapeId = change.shapeId ? shapeIds.get([...path, change.shapeId].join("/")) : undefined;
  if (groupPath.some((id) => !id) || (change.shapeId && !shapeId)) {
    throw new Error(`"${change.description}" targets a shape that is not on the copied slide.`);
  }
  return {
    ...change,
    slideId: copy.slideId,
    ...(change.shapeId ? { shapeId } : {}),
    ...(path.length ? { groupPath: groupPath as string[] } : {}),
  };
}

/** What an edit does, in place of the slide and shape ids it targets. */
function changeSummary(change: ClientChange): string {
  switch (change.type) {
    case "add_table": return "Adds an editable table";
    case "update_table_cells": return "Updates table cells";
    case "format_table_cells": return "Formats table cells";
    case "insert_table_rows": return "Inserts table rows";
    case "delete_table_rows": return "Deletes table rows";
    case "insert_table_columns": return "Inserts table columns";
    case "delete_table_columns": return "Deletes table columns";
    case "set_text": return "Replaces the text";
    case "format_shape": return "Changes the formatting";
    case "move_resize_shape": return "Moves or resizes it";
    case "add_text_box": return "Adds a text box";
    case "add_rectangle": return "Adds a shape";
    case "delete_shape": return "Removes a shape";
    case "delete_slide": return "Removes the slide";
    default: return "";
  }
}

function exposeChanges(changes: ClientChange[]): void {
  state.pendingChanges = changes || [];
  // New changes lift a held Apply, and with it the line asking for them.
  if (state.applyNeedsReview) $("#change-progress").textContent = "";
  state.applyNeedsReview = false;
  const hasPendingChanges = state.pendingChanges.length > 0;
  const list = $("#change-list");
  list.replaceChildren();

  // Grouped, because approving a new slide and approving an edit to a slide
  // you already have are different decisions.
  const added = state.pendingChanges.filter((change) => change.type === "add_slide");
  const edits = state.pendingChanges.filter((change) => change.type !== "add_slide");
  // The size change comes first because it happens first, and because it
  // touches every slide the user already has.
  state.canvasConversionShown = added.some(isSydaniSlide) && canvasConversionNeeded(state.presentation);
  if (state.canvasConversionShown) {
    const heading = changeGroupHeading("SLIDE SIZE");
    heading.classList.add("canvas-group");
    list.append(heading);
    list.append(canvasConversionRow(state.presentation));
  }
  const editRows: EditThumbnailRow[] = [];
  const copyRows: Array<{ change: ClientChange; label: HTMLElement }> = [];
  if (added.length) {
    list.append(changeGroupHeading("NEW SLIDES"));
    added.forEach((change) => {
      const rendered = changeRow(change, null);
      list.append(rendered.row);
      if (!change.copyOf) return;
      // A copy looks like its source, so the source is what the row shows.
      editRows.push({ change: { ...change, slideId: change.copyOf }, slideNumber: null, thumb: rendered.thumb });
      const label = rendered.row.querySelector(".change-item-design");
      if (label instanceof HTMLElement) copyRows.push({ change, label });
    });
  }
  if (edits.length) {
    list.append(changeGroupHeading("EDITS TO YOUR DECK"));
    edits.forEach((change) => {
      const slideNumber = slideNumberOf(change);
      const rendered = changeRow(change, slideNumber);
      editRows.push({ change, slideNumber, thumb: rendered.thumb });
      list.append(rendered.row);
    });
  }

  const parts: string[] = [];
  if (state.canvasConversionShown) parts.push("4:3 slide size");
  if (added.length) parts.push(`${added.length} new slide${added.length === 1 ? "" : "s"}`);
  if (edits.length) parts.push(`${edits.length} edit${edits.length === 1 ? "" : "s"}`);
  $("#change-count").textContent = parts.join(" · ") || "Nothing to review";
  $("#change-actions").classList.toggle("hidden", !hasPendingChanges);
  $("#apply-changes-button").disabled = !hasPendingChanges || state.busy || state.applying;
  syncComposerState();
  if (editRows.length) void loadEditThumbnails(editRows);
  if (copyRows.length) void labelCopies(copyRows);
  if (hasPendingChanges) {
    // The review card follows the latest response in the shared scroll
    // surface. Wait for its rows to enter layout before revealing its foot.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollConversationToEnd();
      });
    });
  }
}

/** The slide an edit lands on, so the row can show which one it is. */
function slideNumberOf(change: ClientChange): number | null {
  const presentation = state.presentation;
  const slides = presentation?.slides;
  const id = change.slideId;
  if (!id) return null;
  const index = Array.isArray(slides) ? slides.findIndex((slide) => slide.id === id) : -1;
  if (index >= 0) return index + 1;
  if (presentation?.selectedSlideId === id) {
    return presentation.selectedSlideNumber ?? null;
  }
  return null;
}

async function sendPrompt(
  message: string,
  {
    skipUserMessage = false,
    usageEntry,
  }: { skipUserMessage?: boolean; usageEntry?: ChatEntry } = {},
): Promise<void> {
  if (!state.ready || state.busy || state.applying) return;
  const outcomes = undeliveredOutcomes();
  const turnEntryStart = state.chatHistory.length;
  const submittedFiles = [...state.files];
  let initiatingEntry = usageEntry;
  const turnController = new AbortController();
  activeTurnController = turnController;
  setBusy(true);
  state.files = [];
  renderFiles();
  const cachedComponents = selectedComponentAttachments();
  const fileAttachments = submittedFiles.map((file): UiAttachment => ({
    kind: "file", name: file.name, size: file.size,
  }));
  if (!skipUserMessage) {
    initiatingEntry = addMessage("user", message, true,
      [...cachedComponents, ...fileAttachments]);
  }
  let loading = addLoadingIndicator("Checking presentation");
  let activityGroup: ActivityGroupHandle | null = null;
  let activeClientStep: ActivityStepHandle | null = null;
  let activeStorylineStep: ActivityStepHandle | null = null;
  let storyline: StorylineHandle | null = null;
  let updatingStoryline = false;
  let hadActivity = false;
  // Closes a poll still waiting for an answer when the turn behind it ends.
  const openPoll: { expire: (() => void) | null } = { expire: null };
  // The model can reason for a minute or more between steps. With the last
  // row finished and nothing moving, the pane looked frozen. Once nothing has
  // arrived for a moment, the generic row says so instead. It is the same
  // transient indicator as the others, so it never enters the history.
  let thinkingTimer: ReturnType<typeof setTimeout> | undefined;
  let thinkingRow: HTMLElement | null = null;
  const awaitModel = (): void => {
    clearTimeout(thinkingTimer);
    thinkingTimer = setTimeout(() => {
      if (turnController.signal.aborted) return;
      if (!loading.isConnected) loading = addLoadingIndicator();
      updateLoadingIndicator(loading, "Thinking");
      thinkingRow = loading;
    }, THINKING_DELAY_MS);
  };
  const stopThinking = (): void => {
    clearTimeout(thinkingTimer);
    if (thinkingRow === loading) loading.remove();
    thinkingRow = null;
  };
  turnController.signal.addEventListener("abort", stopThinking, { once: true });
  const slideNumberById = new Map<string, number>();
  if (state.presentation?.selectedSlideId && state.presentation?.selectedSlideNumber) {
    slideNumberById.set(state.presentation.selectedSlideId, state.presentation.selectedSlideNumber);
  }
  try {
    // A refresh that fails falls back to the last snapshot. One that never
    // answers stops the send instead: the turn's own reads would hang too.
    // The one read that includes the selected shapes, so "edit this" knows what this is.
    const presentationRefresh = refreshPresentationProfile({ selectedShapes: true }).then(() => {}, () => {
      // Use the most recent selection snapshot when PowerPoint cannot refresh.
    });
    const historySave = initiatingEntry ? flushChatHistory() : Promise.resolve();
    await Promise.all([
      withinTime(presentationRefresh, PRESENTATION_CHECK_MS, POWERPOINT_SILENT, turnController.signal),
      withinTime(historySave, null, "", turnController.signal),
    ]);
    if (initiatingEntry && !skipUserMessage) {
      reconcileUserMessageAttachments(initiatingEntry, [
        ...selectedComponentAttachments(),
        ...fileAttachments,
      ]);
    }
    updateLoadingIndicator(loading, submittedFiles.length ? "Reading attached files" : "Working");
    const files = await Promise.all(submittedFiles.map(fileToPayload));
    if (!submittedFiles.length) awaitModel();
    const payload = await runAgentSession({
      message,
      files,
      previousResponseId: state.previousResponseId,
      presentationContext: presentationContext(outcomes.map((entry) => String((entry as DynamicRecord).text))),
      ...(initiatingEntry?.id && state.activeConversationId && state.presentationId ? {
        entryId: initiatingEntry.id,
        conversationId: state.activeConversationId,
        presentationId: state.presentationId,
      } : {}),
    }, (activity) => {
      clearTimeout(thinkingTimer);
      if (activity.phase === "assistant_message") {
        loading.remove();
        const text = String(activity.message || "").trim();
        if (text) addMessage("assistant", text);
        loading = addLoadingIndicator("Working");
        awaitModel();
        return;
      }
      if (activity.phase === "activity_round_complete") {
        completeActivityGroup(activityGroup);
        activityGroup = null;
        if (thinkingRow !== loading) updateLoadingIndicator(loading, "Working");
        awaitModel();
        return;
      }
      if (activity.phase === "plan_start") {
        loading.remove();
        updatingStoryline = storyline !== null;
        if (!activityGroup) activityGroup = addActivityGroup();
        activeStorylineStep = addActivityStep(
          activityGroup,
          "plan_slides",
          updatingStoryline ? "Updating storyline" : "Writing storyline",
          true,
        );
        hadActivity = true;
        return;
      }
      if (activity.phase === "plan") {
        loading.remove();
        hadActivity = true;
        const isRevision = updatingStoryline || storyline !== null;
        if (!activityGroup) activityGroup = addActivityGroup();
        if (activeStorylineStep) {
          completeActivityStep(
            activeStorylineStep,
            isRevision ? "Updated storyline" : "Wrote storyline",
          );
        } else {
          addActivityStep(
            activityGroup,
            "plan_slides",
            isRevision ? "Updated storyline" : "Wrote storyline",
          );
        }
        completeActivityGroup(activityGroup);
        activityGroup = null;
        activeStorylineStep = null;
        updatingStoryline = false;
        if (storyline) supersedeStoryline(storyline);
        const built = Number(activity.built) || 0;
        storyline = addStoryline(
          activity.slides || [],
          built,
          Array.isArray(activity.designs) ? activity.designs : [],
        );
        markStorylineProgress(storyline, built);
        loading = addLoadingIndicator(toolLabel("find_designs", "while"));
        return;
      }
      if (activity.phase === "draft") {
        loading.remove();
        if (activity.name === "plan_slides") {
          completeActivityStep(
            activeStorylineStep,
            updatingStoryline
              ? "Could not update storyline"
              : "Could not write storyline",
            true,
          );
          activeStorylineStep = null;
          updatingStoryline = false;
          completeActivityGroup(activityGroup);
          activityGroup = null;
          hadActivity = true;
          loading = addLoadingIndicator("Working");
          awaitModel();
          return;
        }
        // The storyline is the deck's progress, so slide-count changes advance
        // it rather than adding another line to the trail.
        const count = activity.result?.slideCount;
        const at = Number(activity.result?.at);
        const design = designLabelOf(activity);
        const added = Array.isArray(activity.result?.added) ? activity.result.added : [];
        for (const slide of added) {
          const slideAt = Number(slide?.at);
          const slideDesign = String(slide?.slideTypeLabel || "").trim();
          if (Number.isFinite(slideAt)) {
            setStorylineDesign(storyline, slideAt, slideDesign);
          }
        }
        if (Number.isFinite(count)) {
          markStorylineProgress(
            storyline,
            count,
            design,
            Number.isFinite(at) ? at : count,
          );
        } else if (design && Number.isFinite(at)) {
          // A replacement can change the section without changing the number
          // of slides. Keep the visible storyline and its stored replay in sync.
          setStorylineDesign(storyline, at, design);
          touchEntry(storyline?.entry as ChatEntry | undefined);
        }
        if (!activityGroup) activityGroup = addActivityGroup();
        const completed = completedDraftActivity(activity);
        addActivityStep(activityGroup, activity.name, completed.text);
        hadActivity = true;
        loading = addLoadingIndicator(toolLabel(activity.name, "while"));
        return;
      }
      if (activity.phase === "start") {
        loading.remove();
        if (!activityGroup) activityGroup = addActivityGroup();
        activeClientStep = addActivityStep(
          activityGroup,
          activity.name,
          ongoingActivity(activity.name, activity.arguments, slideNumberById),
          true,
        );
        hadActivity = true;
        return;
      }
      if (activity.phase === "complete") {
        rememberSlideNumbers(activity.result, slideNumberById);
        completeActivityStep(
          activeClientStep,
          completedActivity(activity, slideNumberById),
        );
        activeClientStep = null;
        awaitModel();
        return;
      }
      if (activity.phase === "error") {
        const attempted = ongoingActivity(
          activity.name,
          activity.arguments,
          slideNumberById,
        ).toLowerCase();
        completeActivityStep(activeClientStep, `Could not finish ${attempted}`, true);
        activeClientStep = null;
        awaitModel();
      }
    }, (questions) => new Promise((resolve, reject) => {
      clearTimeout(thinkingTimer);
      if (!questions.length) {
        reject(new Error("No questions were supplied."));
        return;
      }
      loading.remove();
      completeActivityGroup(activityGroup);
      activityGroup = null;
      state.awaitingAnswer = true;
      syncComposerState();
      let panel: HTMLFormElement;
      let closed = false;
      const cancelPoll = (): void => {
        if (closed) return;
        closed = true;
        openPoll.expire = null;
        turnController.signal.removeEventListener("abort", cancelPoll);
        panel.remove();
        state.awaitingAnswer = false;
        syncComposerState();
        reject(new Error("The request was stopped."));
      };
      openPoll.expire = cancelPoll;
      let answerEntry: ChatEntry | null = null;
      panel = renderQuestions(questions, async (answers) => {
        // The turn may have ended while the form was open or saving. An
        // answer nobody is waiting for must not be recorded as delivered.
        if (closed) return;
        if (!answerEntry) {
          answerEntry = { kind: "poll", answers };
          recordEntry(answerEntry);
        } else {
          (answerEntry as DynamicRecord).answers = answers;
          touchEntry(answerEntry);
        }
        // Keep the form and selected answers available when persistence fails.
        await flushChatHistory();
        if (closed || turnController.signal.aborted) {
          cancelPoll();
          return;
        }
        closed = true;
        openPoll.expire = null;
        panel.replaceWith(renderPollEntry(answerEntry, touchEntry));
        turnController.signal.removeEventListener("abort", cancelPoll);
        state.awaitingAnswer = false;
        syncComposerState();
        updateLoadingIndicator(loading, "Working");
        awaitModel();
        resolve(answers);
      });
      turnController.signal.addEventListener("abort", cancelPoll, { once: true });
      messages.append(panel);
      syncEmptyState();
      scrollConversationToEnd();
      loading = addLoadingIndicator("Waiting for your answer");
    }), turnController.signal, () => markOutcomesDelivered(outcomes));
    clearTimeout(thinkingTimer);
    if (payload.interrupted) {
      loading.remove();
      stopActivityStep(activeStorylineStep);
      stopActivityStep(activeClientStep);
      completeActivityGroup(activityGroup);
      const interrupted = normaliseAssistantResponse(payload);
      // A null id means the server keeps this chat's memory: drop any pointer.
      state.previousResponseId = payload.responseId === null ? null
        : typeof payload.responseId === "string" ? payload.responseId
        : state.previousResponseId;
      state.files = [...submittedFiles, ...state.files];
      renderFiles();
      if (payload.failed && interrupted.message) {
        // The service failed after slides were built; they are offered rather
        // than lost, and the reply says why the turn ended early.
        addMessage("assistant", interrupted.message);
      } else {
        addSystemMessage(
          interrupted.changes
            ? "Stopped. Work completed before the interruption is available to review."
            : "Stopped.",
        );
      }
      const auditTurnId = typeof payload.auditTurnId === "string" ? payload.auditTurnId : null;
      if (initiatingEntry && auditTurnId) initiatingEntry.turnId = auditTurnId;
      tagTurnEntries(turnEntryStart, auditTurnId);
      // A turn with nothing to review leaves an earlier turn's changes, and
      // the turn they are audited against, on the card.
      if (interrupted.changes) {
        state.pendingAuditTurnId = auditTurnId;
        exposeChanges(interrupted.changes as unknown as ClientChange[]);
      }
      return;
    }
    const assistant = normaliseAssistantResponse(payload);
    completeActivityGroup(activityGroup);
    activityGroup = null;
    if (!hadActivity) {
      // "Checked": a file the server could not read is held back, not read.
      if (submittedFiles.length === 1) completeLoadingIndicator(loading, `Checked ${submittedFiles[0]!.name}`);
      else if (submittedFiles.length > 1) completeLoadingIndicator(loading, "Checked attached files");
      else loading.remove();
    } else {
      loading.remove();
    }
    state.previousResponseId = payload.responseId === null ? null
      : typeof payload.responseId === "string" ? payload.responseId
      : state.previousResponseId;
    addMessage("assistant", assistant.message || "Done.");
    const auditTurnId = typeof payload.auditTurnId === "string" ? payload.auditTurnId : null;
    if (initiatingEntry && auditTurnId) initiatingEntry.turnId = auditTurnId;
    tagTurnEntries(turnEntryStart, auditTurnId);
    if (assistant.changes) {
      state.pendingAuditTurnId = auditTurnId;
      exposeChanges(assistant.changes as unknown as ClientChange[]);
    }
  } catch (error) {
    clearTimeout(thinkingTimer);
    loading.remove();
    completeActivityGroup(activityGroup);
    // Signed out mid-turn: the pane has been cleared, so there is nothing to
    // report into and no request to offer again.
    if (!state.authenticated) return;
    if (error instanceof StoppedError) {
      // Stopped while PowerPoint or the save was still being waited on: the
      // message never left, so say so rather than leave it looking sent.
      state.files = [...submittedFiles, ...state.files];
      renderFiles();
      addSystemMessage("Stopped before your message was sent.");
      return;
    }
    if (error instanceof DeadlineError) {
      void auditClientEvent("powerpoint.unresponsive", { phase: "presentation_check", ...powerPointActivity() }, null);
    }
    if ((error as SessionError).resetResponseId) {
      state.previousResponseId = null;
      const conversation = activeConversation();
      if (conversation) conversation.previousResponseId = null;
      saveChatHistory();
    } else if ((error as SessionError).responseId) {
      state.previousResponseId = (error as SessionError).responseId!;
      const conversation = activeConversation();
      if (conversation) conversation.previousResponseId = state.previousResponseId;
      saveChatHistory();
    }
    state.files = [...submittedFiles, ...state.files];
    renderFiles();
    addFailure(error, { message, files: [...submittedFiles], entry: initiatingEntry });
    if (initiatingEntry && (error as SessionError).auditTurnId) {
      initiatingEntry.turnId = (error as SessionError).auditTurnId;
    }
    tagTurnEntries(turnEntryStart, (error as SessionError).auditTurnId || null);
  } finally {
    // A socket error or disconnect ends the turn without an abort, which
    // would otherwise leave its poll on screen and answerable.
    openPoll.expire?.();
    if (activeTurnController === turnController) activeTurnController = null;
    state.awaitingAnswer = false;
    settleStoryline(storyline);
    setBusy(false);
    focusPrompt();
  }
}

const APPLY_SILENT = "PowerPoint did not respond, so nothing was applied. Try again in a moment. "
  + "If it keeps happening, close and reopen Deck Studio.";

/** Changes refused before anything was written; Apply holds until revised ones arrive. */
class PreflightError extends NativeEditError {
  constructor(problems: string[]) {
    const shown = problems.slice(0, 5);
    const more = problems.length - shown.length;
    super(`Nothing was applied: ${shown.join("; ")}${more ? `; and ${more} more problem${more === 1 ? "" : "s"}` : ""}. `
      + "Ask for revised changes, then apply again.");
    this.name = "PreflightError";
  }
}

// How long PowerPoint may take to list its slides after an insert it never confirmed.
const INSERT_CHECK_MS = 10_000;

/**
 * After an insert PowerPoint never confirmed, one short read of the slides
 * says what landed. A batch that arrived whole brings nothing more, so the
 * slides this Apply added are removed again and the deck is as it was before;
 * otherwise more may still arrive, and the slides already there are named.
 */
async function accountForInsert(
  error: UncertainInsertError,
  before: string[],
): Promise<{ error: Error; removed: boolean }> {
  let order: string[];
  try {
    order = await withinTime(slideOrder(), INSERT_CHECK_MS, "");
  } catch {
    return { error, removed: false };
  }
  const added = order.filter((id) => !before.includes(id));
  const landed = error.existingIds ? order.filter((id) => !error.existingIds!.includes(id)).length : null;
  if (added.length && landed !== null && landed === error.expected) {
    try {
      await deleteSlidesById(added);
      return {
        error: new Error(`PowerPoint was too slow to confirm the new slides, so Deck Studio removed the `
          + `${added.length} slide${added.length === 1 ? "" : "s"} it had added. Try again in a moment.`),
        removed: true,
      };
    } catch {
      return {
        error: new UncertainInsertError("PowerPoint did not finish adding the slides, and then did not finish "
          + "removing them. Check the presentation, then ask for revised changes before applying again."),
        removed: false,
      };
    }
  }
  const positions = added.map((id) => order.indexOf(id) + 1);
  return {
    error: new UncertainInsertError(`PowerPoint did not finish adding the slides. ${positions.length
      ? `So far it has added ${slideList(positions)}, and more may still appear.`
      : "None have appeared yet, but they still may."} Check the presentation, then ask for revised `
      + "changes before applying again."),
    removed: false,
  };
}

/** Say where the slides this Apply added landed, once they are in. */
async function reportAddedSlides(insertedIds: string[]): Promise<void> {
  const order = await readAfterWrite(slideOrder, [] as string[]);
  const positions = insertedIds.map((id) => order.indexOf(id) + 1).filter((number) => number > 0);
  const where = positions.length ? `, as ${slideList(positions)}` : "";
  const count = insertedIds.length;
  addOutcome(`Added ${count} slide${count === 1 ? "" : "s"} to the open presentation${where}.`, "done");
}

/** A read made before anything is written, so giving up on it is safe. */
function readBeforeWrite<T>(read: Promise<T>): Promise<T> {
  return withinTime(read, PRESENTATION_CHECK_MS, APPLY_SILENT);
}

/**
 * A read made after something was written. The write has landed, so failing
 * the Apply here would offer it again and invite a second copy; the read is
 * given up on and the Apply carries on without it.
 */
async function readAfterWrite<T, F>(read: () => Promise<T>, fallback: F): Promise<T | F> {
  try {
    return await withinTime(read(), PRESENTATION_CHECK_MS, "PowerPoint is slow to respond.");
  } catch {
    return fallback;
  }
}

async function applyChanges() {
  if (!state.pendingChanges.length || state.busy || state.applying || state.applyNeedsReview) return;
  state.applying = true;
  setBusy(state.busy);
  const auditTurnId = state.pendingAuditTurnId;
  const proposed = [...state.pendingChanges];
  await auditClientEvent("changes.apply.started", {
    changeCount: proposed.length,
    changeTypes: changeTypeCounts(proposed),
  }, auditTurnId);
  const button = $("#apply-changes-button");
  button.disabled = true;
  button.textContent = "Checking targets…";
  try {
    // Every read and write has a deadline. A write past its deadline may
    // still land, so it is not tried again: Apply holds until the user has
    // checked the presentation and revised changes arrive.
    let profile = await readBeforeWrite(refreshPresentationProfile());
    if (proposed.some(isSydaniSlide)
      && canvasConversionNeeded(profile) && !state.canvasConversionShown) {
      // The deck changed shape after the card was drawn. Nothing that reflows
      // existing slides is applied without being on the card first.
      exposeChanges(proposed);
      $("#change-progress").textContent =
        "Adding these slides now changes the slide size. Review it and apply again.";
      await auditClientEvent("changes.apply.held", { reason: "canvas_conversion_not_reviewed" }, auditTurnId);
      button.textContent = "Apply to presentation";
      return;
    }
    // New slides and edits travel different roads: Sydani slides are built
    // from the template by the server and inserted as a file, copies of the
    // user's own slides are duplicated in PowerPoint, and edits are applied
    // through Office.js. Slides go first so edits that target them, copies
    // especially, have something to target.
    const sydani = proposed.filter(isSydaniSlide);
    const copies = proposed.filter((change) => change.type === "add_slide" && change.copyOf);
    let edits = proposed.filter((change) => change.type !== "add_slide");
    let editTargets: number[] = [];
    let insertedCount = 0;
    // The first slide added, shown once Apply is done unless edits follow.
    let firstAdded: string | undefined;
    let editedCount = 0;
    let removedBlankCount = 0;
    let tableStyleLevels: number[] = [];
    let tableRefusals: string[] = [];
    let textRewrites: string[] = [];
    let tableRebands: string[] = [];
    // The last slide placed after each anchor, so slides sharing an anchor
    // land one after another in the order they were proposed.
    const tails = new Map<string, string>();
    let copyQueue = [...copies];
    // Every stage is checked here, in one read, before any of them writes:
    // a change that cannot be made stops the whole Apply while the deck is
    // still as it was, rather than after earlier stages have changed it.
    // Edits land after any slide size change that new Sydani slides bring.
    const landing = sydani.length && profile.pageSetupSupported && profile.isFourByThree === false
      ? SYDANI_SIZE : profile;
    const check = await readBeforeWrite(hedged(() => checkChanges(proposed, landing), READ_HEDGE_MS));
    if (check.problems.length) throw new PreflightError(check.problems);
    const before = check.slideIds;
    if (sydani.length) {
      // New slides are built server-side from the template and inserted whole,
      // so the artwork is the artwork the Sydani team authored rather than a
      // reconstruction of it.
      const wasEmpty = (profile.slideCount ?? 0) <= 1 && Boolean(profile.isProbablyBlank);
      const runs: Array<{ after: string | null; changes: ClientChange[]; built: DeckBuild[] }> = [];
      for (const change of sydani) {
        const after = wasEmpty ? null : change.afterSlideId || null;
        const last = runs.at(-1);
        if (last && last.after === after) last.changes.push(change);
        else runs.push({ after, changes: [change], built: [] });
      }
      // Every batch is built and downloaded before the slide size changes, so
      // a build or a download that fails leaves the presentation untouched.
      button.textContent = "Building deck…";
      let prepared = 0;
      for (const run of runs) {
        run.built = await buildSlides(run.changes.map((change) => change.slide!), {
          offset: prepared,
          total: sydani.length,
          onProgress: (note) => { $("#change-progress").textContent = note; },
        });
        prepared += run.changes.length;
      }
      // The whole template is 4:3. For a populated deck the size change was
      // on the review card, so applying approves it. It happens here rather
      // than at startup so that opening the task pane never reflows a deck.
      profile = await ensureSydaniCanvas(profile);
      button.textContent = "Inserting…";
      const insertedIds: string[] = [];
      let existingIds: string[] = [];
      let remaining = [...sydani];
      for (const [index, run] of runs.entries()) {
        const ours = remaining.slice(0, run.changes.length);
        const later = remaining.slice(run.changes.length);
        const queued = copyQueue;
        const anchor = run.after;
        const startTail = anchor ? tails.get(anchor) : undefined;
        // Slides leave the pending list as each batch lands, so a retry never
        // adds them twice, and whatever was to follow the same slide now
        // follows the last of them. A rolled-back run is pending again whole.
        const placed = (count: number, lastId: string | null) => {
          const follow = (changes: ClientChange[]) => {
            if (!anchor || !lastId) return changes;
            const moved = followOn(changes, anchor, lastId);
            return startTail ? followOn(moved, startTail, lastId) : moved;
          };
          if (anchor && lastId) tails.set(anchor, lastId);
          else if (anchor && startTail) tails.set(anchor, startTail);
          else if (anchor) tails.delete(anchor);
          remaining = [
            ...ours.slice(count).map((change) => (lastId ? { ...change, afterSlideId: lastId } : change)),
            ...follow(later),
          ];
          copyQueue = follow(queued);
          state.pendingChanges = [...remaining, ...copyQueue, ...edits];
        };
        const insertion = await insertSlides(run.changes.map((change) => change.slide!), {
          append: !wasEmpty,
          after: anchor ? startTail || anchor : null,
          built: run.built,
          onPlaced: placed,
          onProgress: (note) => {
            if (note.startsWith("Inserting")) $("#change-progress").textContent = note;
          },
        }).catch(async (error: unknown) => {
          if (!(error instanceof UncertainInsertError)) {
            if (insertedIds.length) await reportAddedSlides(insertedIds);
            throw error;
          }
          void auditClientEvent("powerpoint.unresponsive", { phase: "insert_slides", ...powerPointActivity() }, auditTurnId);
          // A rollback that stalled says what it knows; only an insert is counted.
          if (!error.existingIds) throw error;
          const outcome = await accountForInsert(error, before);
          // Every slide this Apply added is gone again, so all of it is pending.
          if (outcome.removed) state.pendingChanges = [...proposed];
          throw outcome.error;
        });
        if (index === 0) existingIds = insertion.existingIds;
        insertedIds.push(...insertion.insertedIds);
        insertedCount += insertion.inserted;
      }
      state.pendingChanges = [...copyQueue, ...edits];
      exposeChanges(state.pendingChanges);
      let blankFailure: unknown = null;
      if (wasEmpty) {
        removedBlankCount = await dropBlankSlides(existingIds).catch((error: unknown) => {
          // PowerPoint never removed it and now never will: the slide stays.
          if (!(error instanceof WriteStalledError && !error.written)) blankFailure = error;
          return 0;
        });
      }
      await reportAddedSlides(insertedIds);
      firstAdded = insertedIds[0];
      if (blankFailure) throw blankFailure;
    }
    if (copyQueue.length) {
      for (let index = 0; copyQueue.length; index += 1) {
        const change = copyQueue[0]!;
        button.textContent = "Copying…";
        $("#change-progress").textContent = `Copying slide ${index + 1} of ${copies.length}`;
        const anchorId = change.afterSlideId || change.copyOf!;
        if (isCopyId(anchorId)) throw new Error(`"${change.description}" follows a copy that has not been made.`);
        const target = tails.get(anchorId) || anchorId;
        const copy = await withinTime(
          copySlide(change.copyOf!, target),
          COPY_SLIDE_MS, "PowerPoint did not finish copying the slide.",
        ).catch((error: unknown) => {
          if (error instanceof CopyStalledError) {
            void auditClientEvent("powerpoint.unresponsive", { phase: "copy_slide", ...powerPointActivity() }, auditTurnId);
          }
          if (!(error instanceof DeadlineError)) throw error;
          void auditClientEvent("powerpoint.unresponsive", { phase: "copy_slide", ...powerPointActivity() }, auditTurnId);
          // The copy may still land after this, so trying again could make a
          // second one. The user checks the presentation first.
          throw new NativeEditError("PowerPoint did not finish copying a slide. Check the presentation "
            + "for the copy, then ask for revised changes before applying again.");
        });
        const fills = edits.filter((edit) => edit.slideId === change.copyId);
        if (!copy.shapeIds && fills.length) {
          // The copy's shapes did not line up with its source's, so it cannot
          // be filled in. Left alone it would show the source's text as if it
          // were new, so it is removed, with the edits meant for it.
          edits = edits.filter((edit) => edit.slideId !== change.copyId);
          copyQueue = followOn(copyQueue.slice(1), change.copyId!, target);
          state.pendingChanges = [...copyQueue, ...edits];
          const order = await readAfterWrite(slideOrder, null);
          const which = order ? `slide ${order.indexOf(change.copyOf!) + 1}` : "the slide";
          await deleteSlidesById([copy.slideId]).catch(() => {
            throw new NativeEditError(`Deck Studio copied ${which} but could not fill in its text, and PowerPoint `
              + "did not finish removing the copy. Check the presentation, then ask for revised changes before "
              + "applying again.");
          });
          addOutcome(`Deck Studio copied ${which} but could not fill in its text, so it removed the copy. Ask for `
            + "the slide again, or duplicate it yourself (right-click the slide, then Duplicate Slide) and ask "
            + "for it to be filled in.");
          continue;
        }
        // The copy exists from here on. Before anything else can fail, it
        // leaves the pending list, or a retry would make a second one.
        tails.set(anchorId, copy.slideId);
        firstAdded ??= copy.slideId;
        const unfilled: ClientChange[] = [];
        edits = edits.flatMap((edit) => {
          if (edit.slideId !== change.copyId) return [edit];
          if (!copy.shapeIds) { unfilled.push(edit); return []; }
          try {
            return [retarget(edit, new Map([[change.copyId!, copy]]))];
          } catch {
            unfilled.push(edit);
            return [];
          }
        });
        copyQueue = followOn(followOn(copyQueue.slice(1), change.copyId!, copy.slideId), anchorId, copy.slideId);
        state.pendingChanges = [...copyQueue, ...edits];
        insertedCount += 1;
        const order = await readAfterWrite(slideOrder, null);
        const where = order
          ? `Copied slide ${order.indexOf(change.copyOf!) + 1} to slide ${order.indexOf(copy.slideId) + 1}.`
          : "Copied a slide.";
        addOutcome(unfilled.length
          ? `${where} Its text could not be filled in, so it still shows the original slide's text.`
          : where, "done");
      }
      exposeChanges(edits);
    }
    if (edits.length) {
      // Numbered after any inserts, so they name where the edits landed. With
      // nothing inserted, the profile read at the start is still current.
      if (insertedCount) profile = await readAfterWrite(refreshPresentationProfile, profile);
      editTargets = [...new Set(edits.map(slideNumberOf)
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value)))];
      // Removals go last, so a change PowerPoint refuses part way never leaves
      // a slide stripped of what it was meant to replace.
      const removal = (change: ClientChange) => change.type === "delete_shape" || change.type === "delete_slide";
      const applying = [...edits.filter((change) => !removal(change)), ...edits.filter(removal)];
      const applied = await applyNativeActions(applying, profile, (current, total, description) => {
        state.pendingChanges = applying.slice(current);
        button.textContent = `Applying ${current}/${total}`;
        $("#change-progress").textContent = description;
      });
      editedCount = applied.actionCount;
      tableStyleLevels = applied.tableStyleLevels;
      tableRefusals = applied.tableRefusals;
      textRewrites = applied.textRewrites;
      tableRebands = applied.tableRebands;
      const shown = applying.find((change) => typeof change.slideId === "string")?.slideId;
      // Not waited on: a redraw that PowerPoint drops costs nothing but looks.
      if (shown) void withinTime(showSlide(shown), PRESENTATION_CHECK_MS, "").catch(() => {});
    } else if (firstAdded) {
      // Shown so the user sees what was added, and so PowerPoint for the web
      // draws it: once, its canvas stayed blank after a new slide was clicked.
      void withinTime(showSlide(firstAdded), PRESENTATION_CHECK_MS, "").catch(() => {});
    }
    const appliedCount = insertedCount + editedCount;
    exposeChanges([]);
    if (!await readAfterWrite(refreshPresentationProfile, null)) {
      addSystemMessage("The changes were applied, but the presentation view could not be refreshed.");
    }
    if (editedCount) {
      const target = editTargets.length === 1 ? ` to slide ${editTargets[0]}` : " to the open presentation";
      addOutcome(
        `Applied ${editedCount} change${editedCount === 1 ? "" : "s"}${target}.`,
        "done",
      );
    }
    if (removedBlankCount) {
      addOutcome("Removed the empty slide the presentation opened with.", "note", "remove");
    }
    addMessage("assistant", "Done. The approved changes are now in the presentation.");
    await auditClientEvent("changes.apply.completed", {
      proposedChanges: proposed.length,
      appliedChanges: appliedCount,
      addedSlides: sydani.length + copies.length,
      nativeEdits: edits.length,
      // How much of the Sydani table style PowerPoint took, per new table: 0 is all of it.
      ...(tableStyleLevels.length ? { tableStyleLevels } : {}),
      ...(tableRefusals.length ? { tableRefusals } : {}),
      // How much of each replaced text was rewritten, so formatting loss shows.
      ...(textRewrites.length ? { textRewrites } : {}),
      // Whether row shading was restored after rows changed, and if not, why.
      ...(tableRebands.length ? { tableRebands } : {}),
    }, auditTurnId);
    state.pendingAuditTurnId = null;
    button.textContent = "Apply to presentation";
    $("#change-progress").textContent = "";
  } catch (error) {
    if (error instanceof DeadlineError) {
      void auditClientEvent("powerpoint.unresponsive", { phase: "apply_check", ...powerPointActivity() }, auditTurnId);
    }
    if (error instanceof WriteStalledError) {
      void auditClientEvent("powerpoint.unresponsive", { phase: "apply_write", ...powerPointActivity() }, auditTurnId);
    }
    const message = errorMessage(error, "The changes could not be applied.");
    await auditClientEvent("changes.apply.failed", {
      changeCount: proposed.length,
      changeTypes: changeTypeCounts(proposed),
      error: message,
      errorType: error instanceof Error ? error.name : "Error",
      ...(error instanceof NativeEditError && error.detail ? { detail: error.detail } : {}),
    }, auditTurnId);
    exposeChanges(state.pendingChanges);
    // Changes refused up front, and writes PowerPoint may or may not have
    // made, wait for revised changes: the agent hears why with the next message.
    state.applyNeedsReview = error instanceof NativeEditError || error instanceof UncertainInsertError
      || (error instanceof WriteStalledError && error.written);
    addOutcome(error instanceof PreflightError ? message : `The remaining changes could not be applied: ${message}`);
    button.textContent = "Apply to presentation";
    $("#change-progress").textContent = state.applyNeedsReview
      ? "Review the presentation and request revised changes before applying again." : "";
  } finally {
    state.applying = false;
    setBusy(state.busy);
  }
}

function discardChanges() {
  if (state.applying || state.busy) return;
  const auditTurnId = state.pendingAuditTurnId;
  const discarded = [...state.pendingChanges];
  void auditClientEvent("changes.discarded", {
    changeCount: discarded.length,
    changeTypes: changeTypeCounts(discarded),
  }, auditTurnId);
  exposeChanges([]);
  state.pendingAuditTurnId = null;
  $("#change-progress").textContent = "";
  addOutcome("The remaining proposed changes were discarded.");
}

async function ensureSydaniCanvas(profile: PresentationProfile) {
  // Every Sydani design is 10 x 7.5 inches. Inserting one into a 16:9 file
  // stretches it, so the canvas is converted first. Callers reach this only
  // once the user has approved the change on the review card, with the
  // profile they have just read.
  if (!profile.supported) throw new Error(profile.reason);
  if (!profile.pageSetupSupported) {
    throw new Error("This PowerPoint version cannot read the slide size, so the "
      + "Sydani 4:3 canvas could not be applied safely.");
  }
  if (profile.isFourByThree) return profile;

  const hadContent = !profile.isProbablyBlank;
  await setSydaniPageSize();
  const converted = await readAfterWrite(refreshPresentationProfile, null);
  if (converted && !converted.isFourByThree) {
    throw new Error("PowerPoint did not apply the required 4:3 slide size.");
  }
  // Converting a populated file reflows what was already in it, so it is
  // reported rather than done quietly.
  addOutcome(hadContent
    ? "This presentation was not 4:3 and has been changed to the Sydani canvas. "
      + "Every Sydani design is 10 x 7.5 inches, so slides that were already here "
      + "will have reflowed."
    : "Set to the Sydani 4:3 canvas.");
  return converted || profile;
}

// PowerPoint on the web has been seen loading every file of the pane and then
// never reporting Office as ready. Say so instead of spinning forever.
const OFFICE_START_MS = 30_000;

async function initialise() {
  const startWatch = setTimeout(() => {
    if (!document.body.classList.contains("host-pending")) return;
    $("#app-loading-message").textContent =
      "PowerPoint has not finished starting Deck Studio. Close this pane and open Deck Studio again.";
  }, OFFICE_START_MS);
  Office.onReady(async ({ host }) => {
    clearTimeout(startWatch);
    document.body.classList.remove("host-pending");
    if (host !== Office.HostType.PowerPoint) {
      document.body.classList.remove("authenticated", "powerpoint-host");
      document.body.classList.add("browser-host");
      return;
    }
    document.body.classList.remove("browser-host");
    document.body.classList.add("powerpoint-host");
    trackPowerPointRuns();
    if (!Office.context.requirements.isSetSupported("PowerPointApi", "1.4")) {
      setCompatibilityNotice("This PowerPoint version cannot create native editable shapes. Update Microsoft 365 to continue.");
      return;
    }
    $("#login-button").disabled = false;
    showAppLoading("Checking your account…");
    try {
      await configureMicrosoftSignIn();
      await restoreAuthentication();
    } catch {
      showSignedOut();
      setCompatibilityNotice("Deck Studio is unavailable right now. Please try again in a moment.");
    }
  });
}

$("#microsoft-login-button").addEventListener("click", () => void microsoftLogin(true));

$("#login-form").addEventListener("submit", async (event: SubmitEvent) => {
  event.preventDefault();
  await signIn();
});
$("#toggle-password").addEventListener("click", () => {
  const password = $("#login-password");
  const visible = password.type === "text";
  password.type = visible ? "password" : "text";
  $("#toggle-password").setAttribute("aria-label", visible ? "Show password" : "Hide password");
  password.focus();
});
$("#login-email").addEventListener("input", () => setLoginError(null));
$("#login-password").addEventListener("input", () => setLoginError(null));
$("#sign-out-button").addEventListener("click", () => void signOut());
$("#retry-history-save").addEventListener("click", async () => {
  const button = $("#retry-history-save");
  button.disabled = true;
  try { await flushChatHistory(); } catch { /* the persistence notice stays visible */ }
  finally { button.disabled = false; }
});
window.addEventListener("online", () => { void flushChatHistory().catch(() => {}); });

function attachmentLimitMessage(incoming: File[], alreadyAttached: boolean): string {
  const megabytes = (bytes: number) => `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB`;
  const single = incoming.length === 1 ? incoming[0]! : null;
  if (alreadyAttached) {
    return `Adding ${single ? single.name : "these files"} would take your attachments over the 20 MB limit. `
      + "Remove a file or attach something smaller.";
  }
  return single
    ? `${single.name} is ${megabytes(single.size)}, over the 20 MB attachment limit. Attach a smaller file.`
    : "Together these files are over the 20 MB attachment limit. Attach a smaller set.";
}

// The binary formats from before Office 2007, which the AI service cannot
// read, and what to save each as instead.
const OLD_OFFICE_FORMATS = new Map([
  ["ppt", { app: "PowerPoint", save: ".pptx" }],
  ["xls", { app: "Excel", save: ".xlsx" }],
]);

function oldOfficeFormat(file: File) {
  const extension = /\.([a-z]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  return extension ? OLD_OFFICE_FORMATS.get(extension) : undefined;
}

function oldFormatMessage(files: File[]): string {
  if (files.length === 1) {
    const format = oldOfficeFormat(files[0]!)!;
    return `${files[0]!.name} is in an old Office format that Deck Studio cannot read. Open it in `
      + `${format.app}, save it as ${format.save}, and attach that instead.`;
  }
  return `${files.map((file) => file.name).join(", ")} are in old Office formats that Deck Studio cannot `
    + "read. Save each one as .pptx or .xlsx, and attach those instead.";
}

$("#files").addEventListener("change", (event: Event) => {
  const input = event.target as HTMLInputElement;
  const picked = [...(input.files || [])];
  const old = picked.filter((file) => oldOfficeFormat(file));
  if (old.length) addSystemMessage(oldFormatMessage(old));
  const incoming = picked.filter((file) => !oldOfficeFormat(file));
  if (!incoming.length) {
    input.value = "";
    focusPrompt();
    return;
  }
  const total = [...state.files, ...incoming].reduce((sum, file) => sum + file.size, 0);
  if (total > 20 * 1024 * 1024) {
    addSystemMessage(attachmentLimitMessage(incoming, state.files.length > 0));
    input.value = "";
    focusPrompt();
    return;
  }
  state.files.push(...incoming);
  input.value = "";
  renderFiles();
  // Attaching a file is never the last thing someone wants to do; they are
  // about to say what to do with it. The OS picker leaves focus on the button,
  // so put it back in the box they are about to type in.
  focusPrompt();
});

$("#prompt").addEventListener("input", () => {
  resizePrompt();
  syncComposerState();
});
$("#prompt").addEventListener("keydown", (event: KeyboardEvent) => {
  if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    $("#composer").requestSubmit();
  }
});

$("#send-button").addEventListener("click", (event: MouseEvent) => {
  if (!state.busy) return;
  event.preventDefault();
  // Cancellation belongs only to an explicit activation of the stop control.
  // Form submission—including Enter in an empty textarea—never reaches it.
  if (state.awaitingAnswer || !$("#prompt").value.trim()) stopActiveTurn();
});

$("#composer").addEventListener("submit", async (event: SubmitEvent) => {
  event.preventDefault();
  const prompt = $("#prompt");
  if (state.busy) return;
  const message = prompt.value.trim();
  if (!message) return;
  prompt.value = "";
  resizePrompt();
  syncComposerState();
  await sendPrompt(message);
});

$("#apply-changes-button").addEventListener("click", applyChanges);
$("#discard-changes-button").addEventListener("click", discardChanges);
$("#chat-history-button").addEventListener("click", () => {
  const panel = $("#conversation-panel");
  const willOpen = panel.classList.contains("hidden");
  panel.classList.toggle("hidden", !willOpen);
  $("main").classList.toggle("chats-open", willOpen);
  $("#chat-history-button").setAttribute("aria-expanded", String(willOpen));
  if (willOpen) renderConversationList();
});
$("#new-chat-button").addEventListener("click", createNewConversation);
$("#close-conversation-panel").addEventListener("click", () => {
  closeConversationPanel();
  focusPrompt();
});
document.addEventListener("pointerdown", (event) => {
  const panel = $("#conversation-panel");
  if (panel.classList.contains("hidden")) return;
  if (panel.contains(event.target) || $("#chat-history-button").contains(event.target)) return;
  closeConversationPanel();
});
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || $("#conversation-panel").classList.contains("hidden")) return;
  closeConversationPanel();
  focusPrompt();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") void flushChatHistory().catch(() => {});
});

initialise();
